<template>
  <div
    id="edit_deal"
    class="flex-v-full">
    <Breadcrumbs :items="breadcrumbs" />
    <div
      v-if="deal != null"
      id="edit_package"
      class="container-fluid flex-v-full">
      <div class="flex-v-full">
        <div class="form-horizontal flex pr-0">
          <div class="v-scroll form-wrapper">
            <div style="display: flex; flex-direction: column; height: 100%">
              <div
                class="px-4"
                style="flex: 1">
                <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                <!-- PURCHASE TYPE -->
                <deal-purchase-type-form
                  v-if="!isCuratorOrganization"
                  :purchase-type="purchaseType"
                  :purchase-types="purchaseTypes"
                  :can-edit-purchase-type="canEditPurchaseType"
                  @on-purchase-type-changed="onPurchaseTypeChanged" />

                <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                <!-- NEW DEAL -->
                <deal-form
                  :is-new="isNew"
                  :show-deal-scope="showDealScope"
                  :is-flexible="isFlexible"
                  :is-purchase-type-cadence="isPurchaseTypeCadence"
                  :show-unit-information-in-bid-requests="showUnitInformationInBidRequests"
                  @on-flexibility-changed="(flexibility) => (isFlexible = flexibility)"
                  @on-handle-package-custom-property-change="handlePackageCustomPropertyChange"
                  @on-handle-package-custom-property-search-change="handlePackageCustomPropertySearchChange" />

                <tab-list
                  v-if="canShowCrossSSP && isNew && !isCopiedDeal && organizationExternalSSPs.length > 0"
                  id="curation-tabs"
                  ref="tabsList"
                  :tabs="tabOptions"
                  @tab-changed="(tab) => (dealCreationMode = tab.id)" />

                <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                <!-- DEAL AVAILABILITY -->
                <FormSection
                  :icon="IconColoredBuyer"
                  :title="capitalize(i('names.dealAvailability'))">
                  <deal-availability-dates
                    :is-new="isNew"
                    :is-n-g-or-preferred-deal="isNGOrPreferredDeal"
                    @refresh-estimated-delivery-stats="refreshEstimatedDeliveryStats" />

                  <EditDealBuyerOption
                    v-if="loadBuyerOptions && dealCreationMode === DealCreationModes.DEFAULT"
                    :deal-locked-scope-type="dealLockedScopeType"
                    :is-internal-d-s-p-selected="isInternalDSPSelected"
                    :is-external-d-s-p-selected="isExternalDSPSelected"
                    :is-d-v360-exchange-selected="isDV360ExchangeSelected"
                    :is-xandr-exchange-selected="isXandrExchangeSelected"
                    :is-bell-exchange-selected="isBellExchangeSelected"
                    :is-d-v360-buyer-selected="isDV360BuyerSelected"
                    :is-xandr-buyer-selected="isXandrBuyerSelected"
                    :is-bell-buyer-selected="isBellBuyerSelected"
                    :show-d-v-buyer-warning="showDVBuyerWarning"
                    :is-evergreen-deal="isEvergreenDeal"
                    :is-new="isNew"
                    :is-n-g-or-preferred-deal="isNGOrPreferredDeal"
                    :is-n-g-external-or-preferred-external-deal="isNGExternalOrPreferredExternalDeal"
                    :is-p-g-deal="isPGDeal"
                    :is-missing-some-selected-exchange="isMissingSomeSelectedExchange"
                    :is-missing-all-selected-exchange="isMissingAllSelectedExchange"
                    :organization-external-exchanges="organizationExternalExchanges"
                    :buyer-organization-d-s-ps-for-restricted-deals="buyerOrganizationDSPsForRestrictedDeals"
                    :buyer-organization-d-s-ps="buyerOrganizationDSPs"
                    :selected-buyer-option="selectedBuyerOptionInternal"
                    :selected-buyer-organization-d-s-ps="selectedBuyerOrganizationDSPs"
                    :selected-restricted-buyer-organization-d-s-ps="selectedRestrictedBuyerOrganizationDSPs"
                    :selected-buyer-external-exchanges="selectedRestrictedBuyerExternalExchanges"
                    :curator-organizations="curatorOrganizations"
                    :selected-curator-organizations="selectedRestrictedCuratorOrganizations"
                    :is-supply-organization="isSupplyOrganization"
                    @selected-buyer-option-changed="(val) => (selectedBuyerOption = val)"
                    @selected-restricted-buyer-organization-d-s-ps-changed="
                      onSelectedRestrictedBuyerOrganizationDSPsChanged
                    "
                    @selected-buyer-organization-d-s-ps-changed="onSelectedRestrictedBuyerOrganizationDSPsChanged"
                    @selected-buyer-external-exchanges-changed="onSelectedBuyerExternalExchangesChanged"
                    @selected-curator-organizations-changed="onSelectedRestrictedCuratorOrganizationsChanged" />
                </FormSection>

                <template v-if="dealCreationMode === DealCreationModes.DEFAULT">
                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- SUSTAINABILITY (Curators only) -->
                  <deal-sustainability-form v-if="deal.packageType === PackageType.CURATED" />

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- MEDIA OWNERS (Exchange only) -->
                  <div v-if="isExchange && allMediaOwners.length > 1">
                    <div class="form-section group">
                      <span class="icon"><icon-colored-buyer /></span>
                      {{ capitalize(i('names.mediaOwner', 2)) }}
                    </div>
                    <div class="form-section-content">
                      <div class="form-group row">
                        <form-label :is-required="true">{{ capitalize(i('names.mediaOwner', 2)) }}</form-label>
                        <div class="col-sm-8">
                          <selectable-expandable-small-tiles
                            :items="availableMediaOwners"
                            :should-display-labels="true"
                            :should-display-images="true"
                            label-property-name="name"
                            :max-items-to-show="12"
                            image-source-property-name="logoUrl"
                            :selected-items="selectedMediaOwners"
                            :is-multiselect="isExchange && !isGuaranteedDeal"
                            :search-enabled="true"
                            :search-hint="capitalize(i('actions.searchMediaOwners'))"
                            :modal-title="capitalize(i('actions.browseMediaOwners'))"
                            @selection-changed="handleMediaOwnersSelectionChanged" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- MARKUP / FEES (Curators only) -->
                  <deal-markup-form
                    v-if="isCuratorOrganization && deal.isCuratedDeal"
                    :is-copied-deal="isCopiedDeal"
                    :can-show-auction-type="isNGExternalOrPreferredExternalDeal"
                    :can-edit-auction-type="canEditAuctionType"
                    @update:pricing-type="(val) => (pricingType = val)"
                    @update:cpm-fixed="(val) => (cpmFixed = val)" />

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- IMPRESSIONS / BUDGET -->
                  <div v-show="canShowBudgetImpressions">
                    <div class="form-section group">
                      <span class="icon"><icon-colored-budget /></span>
                      {{ capitalize(i('names.impression', 2)) }} /
                      {{ capitalize(i('names.budget', 1)) }}
                      <div class="actions">
                        <div
                          v-show="isFlexible"
                          class="toggle">
                          {{ capitalize(i('states.buyerDefinable')) }}
                          <ToggleButton
                            :value="!impressionsAndBudgetOverridable"
                            class="ml-2 mr-2"
                            :sync="true"
                            @change="toggleImpressionsAndBudget()" />
                          {{ capitalize(i('states.predefined')) }}
                        </div>
                      </div>
                    </div>
                    <div class="form-section-content">
                      <!-- TYPE: BUDGET OR IMPRESSIONS? -->
                      <div class="form-group row">
                        <form-label :is-required="true">{{ capitalize(i('names.campaignType', 1)) }}</form-label>
                        <div class="col-sm-8 deliver-on-tabs-slider">
                          <tabs-slider
                            width="100%"
                            height="4rem"
                            :min-width-to-show-icons="300"
                            :selected-tab-i-d="budgetMode"
                            :tabs="budgetModeTabs"
                            @selected-tab-changed="(x) => (budgetMode = x.id)" />
                        </div>
                      </div>

                      <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                      <!-- BUDGET -->
                      <div v-if="budgetMode === BudgetModes.BUDGET || isPurchaseTypeCadence">
                        <div class="form-group row">
                          <form-label :is-required="true">
                            <span v-if="impressionsAndBudgetOverridable === false">
                              {{ capitalize(i('names.budget', 1)) }}
                            </span>
                            <span v-else>
                              {{ capitalize(i('names.minimum', 1)) }}
                            </span>
                          </form-label>
                          <div
                            v-if="
                              isPurchaseTypeCadence &&
                              (isCheckingAvails || onePlayPerHourDemographicImpressions == null)
                            "
                            class="col-sm-8">
                            <div class="flex-h mt-2 text-muted">
                              <loader-small class="mr-2" />
                              <span v-if="isCheckingAvails">{{
                                i('messages.checkingAvailsToCalculateAccurateBudget')
                              }}</span>
                              <span v-else-if="onePlayPerHourDemographicImpressions == null">{{
                                i('messages.waitingForEstimatedDeliveryStats')
                              }}</span>
                            </div>
                          </div>
                          <div
                            v-else
                            class="col-sm-8">
                            <div class="input-group mb-2 mr-sm-2 mb-sm-0">
                              <div class="input-group-addon">{{ currencySymbol }}</div>
                              <formatted-input
                                id="edit_package_budget_min"
                                name="budgetMin"
                                :disabled="budgetMode !== BudgetModes.BUDGET"
                                :value="budgetMin"
                                :validation-errors="errors"
                                @value-changed="
                                  (val) => {
                                    budgetMin = val;
                                  }
                                " />
                            </div>
                            <span
                              v-show="errors.has('budget')"
                              class="help is-danger"
                              >{{ errors.first('budget') }}</span
                            >
                            <span
                              v-show="errors.has('budgetMin')"
                              class="help is-danger"
                              >{{ errors.first('budgetMin') }}</span
                            >
                            <span
                              v-show="errors.has('minBudget')"
                              class="help is-danger"
                              >{{ errors.first('minBudget') }}</span
                            >
                          </div>
                          <form-label
                            v-if="impressionsAndBudgetOverridable === true"
                            :is-required="true">
                            <span>{{ capitalize(i('names.maximum', 1)) }}</span>
                          </form-label>
                          <div
                            v-if="impressionsAndBudgetOverridable === true"
                            class="col-sm-8">
                            <div class="input-group mb-2 mr-sm-2 mb-sm-0 mt-sm-2">
                              <div class="input-group-addon">{{ currencySymbol }}</div>
                              <formatted-input
                                id="edit_package_budget_max"
                                name="budgetMax"
                                :value="budgetMax"
                                :validation-errors="errors"
                                @value-changed="
                                  (val) => {
                                    budgetMax = val;
                                  }
                                " />
                            </div>
                            <span
                              v-show="errors.has('maxBudget')"
                              class="help is-danger"
                              >{{ errors.first('maxBudget') }}</span
                            >
                            <span
                              v-show="errors.has('budgetMax')"
                              class="help is-danger"
                              >{{ errors.first('budgetMax') }}</span
                            >
                          </div>
                        </div>
                      </div>

                      <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                      <!-- IMPRESSIONS -->
                      <div v-if="budgetMode === BudgetModes.IMPRESSIONS || isPurchaseTypeCadence">
                        <div class="form-group row">
                          <form-label :is-required="true">
                            <span v-if="impressionsAndBudgetOverridable === false">
                              {{ capitalize(i('names.impression', 2)) }}
                            </span>
                            <span v-else>
                              {{ capitalize(i('names.minimum', 1)) }}
                            </span>
                          </form-label>
                          <div
                            v-if="
                              isPurchaseTypeCadence &&
                              (isCheckingAvails || onePlayPerHourDemographicImpressions == null)
                            "
                            class="col-sm-8">
                            <div class="flex-h mt-2 text-muted">
                              <loader-small class="mr-2" />
                              <span v-if="isCheckingAvails">{{
                                i('messages.checkingAvailsToCalculateAccurateImpressions')
                              }}</span>
                              <span v-else-if="onePlayPerHourDemographicImpressions == null">{{
                                i('messages.waitingForEstimatedDeliveryStats')
                              }}</span>
                            </div>
                          </div>
                          <div
                            v-else
                            class="col-sm-8">
                            <formatted-input
                              id="edit_package_impressions_min"
                              name="impressionsMin"
                              :disabled="budgetMode !== BudgetModes.IMPRESSIONS"
                              :value="impressionsMin"
                              :validation-errors="errors"
                              format="0,0"
                              @value-changed="
                                (val) => {
                                  impressionsMin = val;
                                }
                              " />
                            <span
                              v-show="errors.has('impressions')"
                              class="help is-danger"
                              >{{ errors.first('impressions') }}
                            </span>
                            <span
                              v-show="errors.has('minMarketTargetedImpressions')"
                              class="help is-danger"
                              >{{ errors.first('minMarketTargetedImpressions') }}
                            </span>
                          </div>
                        </div>
                        <div
                          v-if="impressionsAndBudgetOverridable === true"
                          class="form-group row mt-sm-3">
                          <form-label :is-required="true">
                            <span>
                              {{ capitalize(i('names.maximum', 1)) }}
                            </span>
                          </form-label>
                          <div class="col-sm-8">
                            <formatted-input
                              id="edit_package_impressions_max"
                              name="impressionsMax"
                              :value="impressionsMax"
                              :validation-errors="errors"
                              format="0,0"
                              @value-changed="
                                (val) => {
                                  impressionsMax = val;
                                }
                              " />
                            <span
                              v-show="errors.has('marketTargetedImpressionsMax')"
                              class="help is-danger"
                              >{{ errors.first('marketTargetedImpressionsMax') }}</span
                            >
                            <span
                              v-show="errors.has('maxMarketTargetedImpressions')"
                              class="help is-danger"
                              >{{ errors.first('maxMarketTargetedImpressions') }}</span
                            >
                          </div>
                        </div>
                      </div>

                      <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                      <!-- CADENCE -->
                      <EditCadence
                        v-if="isPurchaseTypeCadence"
                        ref="editCadenceRef"
                        :avails-result="lastAvailsResult"
                        :cadence="cadence"
                        :original-cadence="deal.originalObject?.cadenceMin"
                        :cadence-smooth-catchup="deal.cadenceSmoothCatchup"
                        :can-edit-cadence="true"
                        :budget-mode="budgetMode"
                        :is-checking-avails="isCheckingAvails"
                        :purchase-type="purchaseType.value"
                        :budget="deal.budgetMin"
                        :impressions="deal.marketTargetedImpressionsMin"
                        :estimated-target-impressions="deal.cpmCalculator.estimatedTargetImpressions"
                        :update-cadence="
                          (value) => {
                            updatePackageCadenceMin({ package: deal, value });
                            updatePackageCadenceMax({ package: deal, value });
                          }
                        "
                        :update-budget="
                          (value) => {
                            updatePackageBudgetMin({ package: deal, value });
                            updatePackageBudgetMax({ package: deal, value });
                          }
                        "
                        :update-impressions="
                          (value) => {
                            updatePackageImpressionsMin({ package: deal, value });
                            updatePackageImpressionsMax({ package: deal, value });
                          }
                        "
                        :update-cadence-smooth-catchup="
                          (value) => {
                            updatePackageCadenceSmoothCatchup({ package: deal, value });
                          }
                        "
                        :is-new="isNew"
                        :audience-impressions-multiplier="audienceImpressionsMultiplier"
                        :one-play-per-hour-demographic-impressions="onePlayPerHourDemographicImpressions"
                        :target-params="estimatedDeliveryStatsRef?.targetParamsEstimatedDeliveryStatistics"
                        :is-saving="isSaveChangesModalVisible"
                        @check-capacity="() => checkAvails()"
                        @changed="onCadenceChanged" />
                    </div>
                  </div>

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- CPM / PRICING -->
                  <deal-pricing-form
                    v-if="!isCuratorOrganization"
                    :is-n-g-external-or-preferred-external-deal="isNGExternalOrPreferredExternalDeal"
                    :is-c-p-m-from-rate-card="isCPMFromRateCard"
                    :auction-type="auctionType"
                    :pricing-type="pricingType"
                    :cpm-fixed="cpmFixed"
                    :can-edit-auction-type="canEditAuctionType"
                    :can-edit-c-p-m-fixed="canEditCPMFixed"
                    :currency-symbol="currencySymbol"
                    @update:is-c-p-m-from-rate-card="toggleCPMFromRateCard"
                    @update:auction-type="(val) => (auctionType = val)"
                    @update:pricing-type="(val) => (pricingType = val)"
                    @update:cpm-fixed="(val) => (cpmFixed = val)" />

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- MOBILE RETARGETING -->
                  <div v-show="!isNGExternalOrPreferredExternalDeal">
                    <div
                      v-if="isMobileRetargetingEnabledForUI && !isNGExternalOrPreferredExternalDeal"
                      class="form-section group">
                      <span class="icon"><icon-colored-mobile-phone-vibrating /></span>
                      {{ capitalize(i('names.mobileCampaign', 1)) }}
                    </div>
                    <div
                      v-if="isMobileRetargetingEnabledForUI"
                      class="form-section-content">
                      <div>
                        <div class="form-group row">
                          <form-label>{{ capitalize(i('messages.enableMobileRetargeting')) }}?</form-label>
                          <div class="col-sm-8">
                            <ToggleButton
                              id="edit_lineitem_enable_mobile_retargeting_toggle"
                              :color="colors.success"
                              :value="enableMobileRetargeting"
                              :labels="{
                                checked: capitalize(i('actions.yes')),
                                unchecked: capitalize(i('actions.no')),
                              }"
                              @change="
                                () => {
                                  enableMobileRetargeting = !enableMobileRetargeting;
                                }
                              " />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- DELIVERY -->
                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <form-separator
                    v-if="isAdDurationSectionVisible || isScreenFrequencyCapSectionEnabled || isDaypartSectionVisible">
                    <template #icon>
                      <icon-colored-delivery />
                    </template>
                    {{ capitalize(i('names.delivery', 1)) }}
                  </form-separator>

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- AD DURATION -->
                  <div v-show="isAdDurationSectionVisible">
                    <div class="form-section group">
                      <span class="icon"><icon-colored-duration /></span>
                      {{ capitalize(i('names.adDuration', 1)) }}
                      <div class="actions">
                        <div
                          v-show="isFlexible"
                          class="toggle">
                          <span>{{ capitalize(i('states.buyerDefinable')) }}</span>
                          <ToggleButton
                            id="toggle_ad_duration_section"
                            class="ml-2 mr-2"
                            :value="isPreferredOrNGInternalDeal ? hasAdDuration : !adDurationOverridable"
                            :sync="true"
                            @change="toggleAdDuration()" />
                          <span>{{ capitalize(i('states.predefined')) }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- NG INTERNAL DEAL ONLY -->
                    <div
                      v-if="isPreferredOrNGInternalDeal && !organizationSettings.enableMoFixedAdDurationOverride"
                      v-show="canShowAdDuration">
                      <div class="form-section-content">
                        <div class="form-group row">
                          <form-label>
                            <span>{{ capitalize(i('names.adDuration', 1)) }}</span>
                          </form-label>
                          <div class="col-sm-8">
                            <div class="input-group mb-2 mr-sm-2 mb-sm-0">
                              <formatted-input
                                id="edit_lineitem_ad_duration"
                                name="adDurationMin"
                                :suffix="i('names.second', 2)"
                                :suffix-style="`flex-grow: 0;`"
                                :value="adDurationMin"
                                :validation-errors="errors"
                                format="0,0"
                                @value-changed="
                                  (val) => {
                                    adDurationMin = val;
                                  }
                                " />
                            </div>
                            <span
                              v-show="errors.has('adDurationMin')"
                              class="help is-danger"
                              >{{ errors.first('adDurationMin') }}</span
                            >
                          </div>
                          <span class="col-sm-1 field-help">
                            <span v-tooltip="capitalize(i('messages.addingAdDurationOnTheDealMayReduceInPlays'))">
                              <icon-info />
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <!-- DEFAULT -->
                    <div
                      v-else
                      v-show="canShowAdDuration"
                      class="form-section-content">
                      <div
                        v-show="isFlexible"
                        class="form-group row">
                        <form-label :is-required="true">
                          {{ capitalize(i('messages.isAdDurationFixed')) }}
                        </form-label>
                        <div class="col-sm-8">
                          <label
                            class="control-label"
                            for="edit_package_ad_duration_type_fixed">
                            <input
                              id="edit_package_ad_duration_type_fixed"
                              v-model="adDurationTypeInternal"
                              type="radio"
                              name="adDurationType"
                              value="fixed" />
                            {{ capitalize(yes) }}
                          </label>
                          <label
                            class="control-label"
                            for="edit_package_ad_duration_type_range">
                            <input
                              id="edit_package_ad_duration_type_range"
                              v-model="adDurationTypeInternal"
                              type="radio"
                              name="adDurationType"
                              value="range" />
                            {{ capitalize(no) }}
                          </label>
                        </div>
                      </div>
                      <div class="form-group row">
                        <form-label>{{ capitalize(i('messages.basedOnScreensAdDuration')) }}</form-label>
                        <div class="col-sm-8">
                          <ToggleButton
                            id="edit_ad_duration_based_on_screens"
                            class="ml-2 mr-2"
                            :color="colors.success"
                            :value="isAdDurationBasedOnScreens"
                            :labels="{
                              checked: capitalize(i18n.global.t('actions.yes')),
                              unchecked: capitalize(i18n.global.t('actions.no')),
                            }"
                            :sync="true"
                            @change="
                              () => {
                                isAdDurationBasedOnScreens = !isAdDurationBasedOnScreens;
                              }
                            " />
                        </div>
                      </div>
                      <div
                        v-show="!isAdDurationBasedOnScreens"
                        class="form-group row">
                        <form-label :is-required="true">
                          <span v-if="adDurationType === 'fixed'">
                            {{ capitalize(i('names.adDuration', 1)) }}
                          </span>
                          <span v-else>
                            {{ capitalize(i('names.minimum', 1)) }}
                          </span>
                        </form-label>
                        <div class="col-sm-8">
                          <div class="input-group mb-2 mr-sm-2 mb-sm-0">
                            <formatted-input
                              id="edit_lineitem_ad_duration"
                              name="adDurationMin"
                              :suffix="i('names.second', 2)"
                              :suffix-style="`flex-grow: 0;`"
                              :value="adDurationMin"
                              :validation-errors="errors"
                              format="0,0"
                              @value-changed="
                                (val) => {
                                  adDurationMin = val;
                                }
                              " />
                          </div>

                          <span
                            v-show="errors.has('adDurationMin')"
                            class="help is-danger"
                            >{{ errors.first('adDurationMin') }}</span
                          >
                          <span
                            v-show="errors.has('minAdDuration')"
                            class="help is-danger"
                            >{{ errors.first('minAdDuration') }}</span
                          >
                        </div>
                      </div>
                      <div
                        v-if="adDurationType === 'range'"
                        class="form-group row mt-sm-3">
                        <form-label :is-required="true">
                          <span>
                            {{ capitalize(i('names.maximum', 1)) }}
                          </span>
                        </form-label>
                        <div class="col-sm-8">
                          <div class="input-group mb-2 mr-sm-2 mb-sm-0">
                            <input
                              v-model="adDurationMax"
                              type="text"
                              name="adDurationMax"
                              :class="{
                                'form-control': true,
                                input: true,
                                'is-danger': errors.has('adDurationMax'),
                              }" />
                            <div class="input-group-addon">{{ i('names.second', 2) }}</div>
                          </div>
                          <span
                            v-show="errors.has('adDurationMax')"
                            class="help is-danger"
                            >{{ errors.first('adDurationMax') }}</span
                          >
                          <span
                            v-show="errors.has('maxAdDuration')"
                            class="help is-danger"
                            >{{ errors.first('maxAdDuration') }}</span
                          >
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- MIN SECONDS BETWEEN PLAYS -->
                  <div
                    v-if="isScreenFrequencyCapSectionEnabled"
                    v-show="!isNGExternalOrPreferredExternalDeal && !isPurchaseTypeCadence">
                    <div class="form-section group">
                      <span class="icon"><icon-colored-wait /></span>
                      {{ capitalize(i('names.screenFrequencyCap', 1)) }}
                      <div class="actions">
                        <div
                          v-show="isFlexible"
                          class="toggle">
                          <span v-if="!isPreferredOrNGInternalDeal">{{ capitalize(i('states.buyerDefinable')) }}</span>
                          <ToggleButton
                            id="toggle_freq_cap_section"
                            class="ml-2 mr-2"
                            :value="
                              isPreferredOrNGInternalDeal
                                ? hasMinSecondsBetweenPlays
                                : !minSecondsBetweenPlaysOverridable
                            "
                            :sync="true"
                            @change="toggleMinSecondsBetweenPlays()" />
                          <span v-if="!isPreferredOrNGInternalDeal">{{ capitalize(i('states.predefined')) }}</span>
                        </div>
                      </div>
                    </div>
                    <div
                      v-show="canShowMinSecondsBetweenPlays"
                      class="form-section-content">
                      <div class="form-group row">
                        <form-label :is-required="true">
                          <span v-if="minSecondsBetweenPlaysType === 'fixed'">
                            {{ capitalize(i('names.screenFrequencyCap', 1)) }}
                          </span>
                          <span v-else>
                            {{ capitalize(i('names.minimum', 1)) }}
                          </span>
                        </form-label>
                        <div class="col-sm-8">
                          <div class="input-group mb-2 mr-sm-2 mb-sm-0">
                            <input
                              v-model="minSecondsBetweenPlaysMin"
                              type="text"
                              name="minSecondsBetweenPlaysMin"
                              :class="{
                                'form-control': true,
                                input: true,
                                'is-danger': errors.has('minSecondsBetweenPlaysMin'),
                              }" />
                            <div class="input-group-addon">{{ i('names.second', 2) }}</div>
                          </div>
                          <span
                            v-show="errors.has('minSecondsBetweenPlaysMin')"
                            class="help is-danger"
                            >{{ errors.first('minSecondsBetweenPlaysMin') }}</span
                          >
                          <span
                            v-show="errors.has('minMinSecondsBetweenPlays')"
                            class="help is-danger"
                            >{{ errors.first('minMinSecondsBetweenPlays') }}</span
                          >
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- TARGETING -->
                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <form-separator>
                    <template #icon>
                      <icon-colored-targeting />
                    </template>
                    {{ capitalize(i('names.targeting', 1)) }}
                  </form-separator>

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- LOCATIONS -->
                  <deal-map
                    ref="dealMapRef"
                    :is-ready="isReady"
                    :map-suppliers="mapSuppliers"
                    :is-flexible="isFlexible"
                    :is-preferred-or-n-g-internal-deal="isPreferredOrNGInternalDeal"
                    @on-overall-map-selections-changed="onOverallMapSelectionsChanged"
                    @on-proximity-geofences-changed="
                      (geofences) => {
                        proximityGeofences = geofences;
                      }
                    " />

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- CHILD DEALS (Curated Only) -->
                  <EditDealChildDeals
                    v-if="isCuratorOrganization"
                    :deal-locked-scope-type="dealLockedScopeType"
                    :active-deal="activeDeal"
                    :suppliers="props.allMediaOwners"
                    @deal-preview-changed="activeDeal = $event"
                    @selection-changed="refreshEstimatedDeliveryStats"
                    @filters-changed="onChildDealsFilterChanged" />

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- Spend Steering (Curate Only) -->
                  <deal-spend-steering-form
                    v-if="isCuratorOrganization"
                    :suppliers="allMediaOwners"
                    :active-deal="activeDeal" />

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- TRIGGERS -->
                  <div class="row mt-4">
                    <div class="col-12">
                      <div
                        v-for="section in selectedAdditionalTargetingCriteria"
                        :key="section.id"
                        class="section">
                        <component
                          :is="section.component"
                          :entity="deal"
                          :can-be-removed="true"
                          :validation-errors="errors"
                          :is-read-only="false"
                          @changed="(value) => onSectionValueUpdated(section, value)"
                          @cleared="() => onSectionCleared(section)" />
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <more-targeting-selector
                        :available-sections="availableSections"
                        :entity="deal"
                        :label-text="capitalize($t('messages.moreTargeting'))"
                        @changed="onSelectedTargetingSectionsChanged" />
                    </div>
                  </div>

                  <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                  <!-- DEMOGRAPHICS -->
                  <demographic-targeting
                    v-show="isNotChina && !shouldDisplayFullScreenMap && !isDemographicsDisabled"
                    :entity="deal"
                    :is-flexible="isFlexible"
                    :is-overridable="!isCuratorOrganization && demographicsOverridable"
                    :is-read-only="!canEditAudienceTargeting"
                    :should-defer-initialization="true"
                    data-entity-type="audience"
                    :enable-audience-files="organizationSettings.uiEnableAudienceFiles"
                    :show-hourly-audience-concentration="true"
                    :show-min-audience-concentration="false"
                    :average-concentration="estimatedAverageConcentration"
                    @min-concentration-applied="updateUnitIDsAndRefresh"
                    @min-concentration-changed="onMinConcentrationFactorChange"
                    @toggled="toggleDemographics"
                    @changed="onDemographicChanged"
                    @error="onDemographicError">
                    <!-- CONCENTRATION LEVEL -->
                    <!-- HIDING IT FOR NOW FE-153(TO BE DELETED IN FUTURE) -->
                    <div
                      v-if="false"
                      class="form-group row">
                      <form-label for-i-d="edit_package_demographics_concentration_level">
                        {{ capitalize(i('names.concentrationLevel', 1)) }}
                      </form-label>
                      <div
                        id="edit_package_demographics_concentration_level"
                        class="col-sm-8">
                        <vue-slider
                          v-model="minConcentrationFactor"
                          :data="[1, 1.5, 2]"
                          :piecewise="true"
                          :piecewise-label="true"
                          :bg-style="{ backgroundImage: '-webkit-linear-gradient(right, #ff0059, #0F78FF)' }"
                          :process-style="{ background: 'transparent' }"
                          tooltip="none"
                          style="margin-bottom: 3.2rem">
                          <template #label="{ label }">
                            <div class="vue-slider-label-marker" />
                            <span
                              v-if="label === 1"
                              class="vue-slider-label left"
                              >{{ capitalize(i('names.default', 1)) }}
                              <span class="field-help">
                                <span
                                  v-tooltip.bottom="
                                    capitalize(i('messages.audienceConcentrationLevelDefaultDescription'))
                                  ">
                                  <icon-info />
                                </span>
                              </span>
                            </span>
                            <span
                              v-if="label === 1.5"
                              class="vue-slider-label"
                              >{{ capitalize(i('messages.highlyConcentrated')) }}</span
                            >
                            <span
                              v-if="label === 2"
                              class="vue-slider-label right"
                              >{{ capitalize(i('messages.veryHighlyConcentrated')) }}</span
                            >
                          </template>
                        </vue-slider>
                      </div>
                    </div>
                  </demographic-targeting>

                  <!-- CUSTOM FIELDS -->
                  <EntityCustomFieldsEditor
                    :organization-custom-fields="organizationCustomFields"
                    :entity-custom-fields="entityCustomFields"
                    :validation-errors="errors"
                    @changed="onCustomFieldsChanged" />
                </template>

                <edit-cross-s-s-p-deal
                  ref="editCrossSSPDealRef"
                  v-if="canShowCrossSSP && dealCreationMode === DealCreationModes.EXTERNAL && isReady"
                  :is-copied-deal="isCopiedDeal"
                  @update:map-selections="onOverallMapSelectionsChanged"
                  @update:proximity-geofences="(geofences) => (proximityGeofences = geofences)"
                  @update:pricing-type="(val) => (pricingType = val)"
                  @update:cpm-fixed="(val) => (cpmFixed = val)" />

                <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
                <!-- AVAILS -->
                <template v-if="isCheckingAvailsAvailable">
                  <transition name="squish fade">
                    <div
                      v-if="isMounted"
                      class="form-section group">
                      <span class="icon"><icon-colored-avails /></span>
                      {{ capitalize(i('names.availability', 1)) }}
                      <experimental-badge v-if="organizationSettings?.uiEnablePredictiveAvails" />
                      <div
                        v-if="organizationSettings?.uiEnablePredictiveAvails"
                        class="col-sm-1 field-help" />
                      <div class="actions">
                        <button
                          class="btn btn-xs btn-default float-right"
                          :disabled="isCheckingAvails"
                          @click="refreshAvails">
                          {{ capitalize(i('actions.checkAvailability')) }}
                        </button>
                      </div>
                    </div>
                  </transition>
                  <transition name="squish fade">
                    <div
                      v-if="isMounted"
                      v-show="
                        isCheckingAvails ||
                        (availsResultRef != null && availsResultRef.checkAvailsResult !== null) ||
                        checkAvailsAlert != null
                      "
                      class="form-section-content">
                      <transition name="fade">
                        <AvailsResult
                          ref="availsResultRef"
                          :organization-i-d="organizationID"
                          :start-date="startDate"
                          :end-date="deal.endDate"
                          :ad-duration="deal.adDurationMin"
                          :budget="budgetMode === BudgetModes.BUDGET ? deal.budgetMin : undefined"
                          :budget-mode="budgetMode"
                          :daypart="deal.daypart"
                          :demographic-i-ds="deal.demographicTargetIDs"
                          :entity="deal"
                          :language-exclusion-i-ds="deal.languageExclusionIDs"
                          :language-i-ds="deal.languageIDs"
                          :location-i-ds="deal.locationTargetIDs"
                          :location-exclusion-i-ds="deal.locationExclusionIDs"
                          :network-i-ds="deal.networkIDs"
                          :site-i-ds="deal.siteIDs"
                          :site-exclusion-i-ds="deal.siteExclusionIDs"
                          :unit-i-ds="deal.unitIDs"
                          :unit-exclusion-i-ds="deal.unitExclusionIDs"
                          :unit-pack-i-ds="deal.unitPackIDs"
                          :unit-pack-exclusion-i-ds="deal.unitPackExclusionIDs"
                          :geofences="proximityGeofences"
                          :geofence-group-i-ds="deal.geofenceGroupIDs"
                          :geofence-group-exclusion-i-ds="deal.geofenceGroupExclusionIDs"
                          :unit-tags="deal.unitTags"
                          :unit-tag-exclusions="deal.unitTagExclusions"
                          :market-i-ds="deal.marketIDs"
                          :priority="5"
                          :creative-formats="deal.creativeFormats"
                          :creative-format-exclusions="deal.creativeFormatExclusions"
                          :creative-orientations="deal.creativeOrientations"
                          :creative-orientation-exclusions="deal.creativeOrientationExclusions"
                          :iab-categories="deal.iabCategories"
                          :iab-category-exclusions="deal.iabCategoryExclusions"
                          :cpm="deal.isCPMFromRateCard ? undefined : deal.cpmCalculator.cpmFixed"
                          :impressions="
                            budgetMode === BudgetModes.BUDGET
                              ? undefined
                              : parseInt(deal.marketTargetedImpressionsMin, 10)
                          "
                          :purchase-type="purchaseType != null ? purchaseType.value : null"
                          :min-seconds-between-plays="deal.minSecondsBetweenPlaysMin"
                          :cadence="deal.cadenceMin"
                          @check-avails-started="
                            () => {
                              isCheckingAvails = true;
                              shouldRefreshCadence = false;
                            }
                          "
                          @check-avails-ended="
                            () => {
                              isCheckingAvails = false;
                            }
                          "
                          @avails-result-changed="onAvailsResultChanged" />
                      </transition>
                      <transition name="squish fade">
                        <div
                          v-if="checkAvailsAlert != null"
                          class="alert alert-danger">
                          {{ checkAvailsAlert }}
                        </div>
                      </transition>
                    </div>
                  </transition>
                </template>
              </div>
              <!-- FOOTER -->
              <save-and-cancel
                class="floating"
                entity-type="package"
                @cancel="cancel"
                @save="save" />
            </div>
          </div>
          <!-- ESTIMATED DELIVERY STATS / SUMMARY -->
          <side-drawer
            v-if="dealCreationMode === DealCreationModes.DEFAULT"
            class="estimated-delivery-stats-container"
            :class="{ 'full-screen-map': shouldDisplayFullScreenMap }"
            :is-active="true">
            <overall-summary
              ref="overallSummary"
              :are-campaign-details-filled-in="false"
              :requires-campaign-creation="null"
              :audience-description="audienceDescription"
              :overall-map="activeMapRef?.overallMapRef"
              :always-on="isEvergreenDeal"
              :start-date="startDate"
              :end-date="endDate" />
            <CuratedDealEstimatedDeliveryStats
              v-if="isCuratorOrganization"
              ref="curatedDealEstimatedDeliveryStatsRef"
              :delivery-statistics="deliveryStatistics"
              :media-owners="allMediaOwners" />
            <EstimatedDeliveryStats
              v-else
              ref="estimatedDeliveryStatsRef"
              :index="audienceIndex"
              :entity="deal"
              :proximity-geofences="proximityGeofences"
              :use-line-item-ad-duration="true"
              :show-creative-sizes="false"
              :show-refresh-cadence-btn="shouldRefreshCadence"
              :has-started-filtering="hasStartedFiltering"
              :is-editing="true"
              :closest-cadence="
                availsResultRef != null && isPurchaseTypeCadence ? formatNumber(deal.cadenceMin, '0, 0.00') : null
              "
              @stats-changed="onStatsChanged"
              @clicked="() => checkAvails()" />
          </side-drawer>
          <!-- DEAL PREVIEW PANE -->
          <side-drawer
            :is-active="activeDeal !== null"
            :is-absolute="true">
            <DealDetailsPane
              :active-deal="activeDeal"
              :suppliers="allMediaOwners"
              @clear-active-deal="activeDeal = null" />
          </side-drawer>
        </div>
      </div>
      <modal
        :id="saveChangesConfirmationModalID"
        :visible="isSaveChangesModalVisible"
        :title="saveChangesConfirmationModalTitle"
        :body="saveChangesConfirmationModalBody"
        :button1-caption="yes"
        :button2-caption="no"
        :button2-visible="true"
        size="half"
        @confirmed="saveChangesModalConfirmed"
        @cancelled="saveChangesModalCancelled">
        <template #modal-after-text>
          <EntityChangeList
            v-if="!isNew && dealChanges?.length > 0"
            :changes="dealChanges" />
          <info-message
            v-if="entityFeeChanged"
            :message="$t('messages.markupAppliedAtMidnight', { local: localMidnightFormatted })" />
        </template>
      </modal>

      <confirm-losing-changes-modal
        :is-visible="isConfirmLosingChangesModalVisible"
        :confirm-callback="confirmLosingChangesCallback"
        @confirmed="
          () => {
            isConfirmLosingChangesModalVisible = false;
          }
        "
        @cancelled="
          () => {
            isConfirmLosingChangesModalVisible = false;
          }
        " />

      <modal
        id="modal_check_avails_unavailable"
        :visible="isCheckAvailsUnavailableModalVisible"
        :title="capitalize(i('actions.checkAvails'))"
        :body="capitalize(i('messages.checkAvailsIsNotAvailable'))"
        :button1-caption="capitalize(i('actions.ok'))"
        :button2-visible="false"
        @confirmed="isCheckAvailsUnavailableModalVisible = false" />

      <modal
        id="modal_xandr_PG_per_rate_warning_modal"
        :visible="isXandrWarningModalVisible"
        :title="capitalize(i('messages.failedToSavePackage'))"
        :body="xandrWarningMessage"
        :button1-caption="capitalize(i('actions.ok'))"
        :button2-visible="false"
        @confirmed="isXandrWarningModalVisible = false"
        @cancelled="isXandrWarningModalVisible = false" />

      <modal
        id="modal_invalid_curated_deal"
        :visible="isIncompatibleSelectionModalVisible"
        :title="capitalize(i('messages.actionRequired'))"
        :button1-caption="capitalize(i('actions.ok'))"
        :button2-visible="false"
        @confirmed="isIncompatibleSelectionModalVisible = false">
        <template #modal-after-text>
          <div>
            <p class="font-weight-bold">{{ capitalize(i('messages.dealHasIncompatibleSelectionHeader')) }}</p>
            <ul>
              <li>{{ capitalize(i('messages.dealHasIncompatiblePDs')) }}</li>
              <li>{{ capitalize(i('messages.dealHasIncompatibleRestrictions')) }}</li>
            </ul>
            <p>{{ capitalize(i('messages.dealHasIncompatibleSelectionConfirmation')) }}</p>
          </div>
        </template>
      </modal>
    </div>
  </div>
</template>

<script setup lang="ts">
  //#region Imports

  import 'vue-slider-component/theme/antd.css';
  import '@/styles/overallmapcontainer.scss';

  import { DateTime } from 'luxon';
  import numbro from 'numbro';
  import {
    capitalize,
    computed,
    ComputedRef,
    defineAsyncComponent,
    inject,
    onBeforeUnmount,
    onMounted,
    provide,
    type Ref,
    ref,
    useTemplateRef,
    watch,
  } from 'vue';
  import { ComponentExposed } from 'vue-component-type-helpers';
  import { onBeforeRouteLeave, useRouter } from 'vue-router';
  import VueSlider from 'vue-slider-component';

  import Colors from '@/Colors';
  import {
    ConfirmLosingChangesModal,
    FormattedInput,
    FormLabel,
    LoaderSmall,
    Modal,
    MoreTargetingSelector,
    SaveAndCancel,
    SelectableExpandableSmallTiles,
    TabsSlider,
  } from '@/common/components/controls';
  import FormSection from '@/common/components/controls/FormSection.vue';
  import FormSeparator from '@/common/components/controls/FormSeparator.vue';
  import ToggleButton from '@/common/components/controls/ToggleButton.vue';
  import DealDetailsPane from '@/common/components/deals/DealDetailsPane.vue';
  import { DealScope, getDealScopeType } from '@/common/components/deals/DealsHelper';
  import EditCrossSSPDeal from '@/common/components/deals/edit/EditCrossSSPDeal.vue';
  import DealAvailabilityDates from '@/common/components/deals/form/DealAvailabilityDates.vue';
  import DealForm from '@/common/components/deals/form/DealForm.vue';
  import DealMap from '@/common/components/deals/form/DealMap.vue';
  import DealMarkupForm from '@/common/components/deals/form/DealMarkupForm.vue';
  import DealPricingForm from '@/common/components/deals/form/DealPricingForm.vue';
  import DealPurchaseTypeForm from '@/common/components/deals/form/DealPurchaseTypeForm.vue';
  import DealSustainabilityForm from '@/common/components/deals/form/DealSustainabilityForm.vue';
  import EditDealChildDeals from '@/common/components/deals/form/EditDealChildDeals.vue';
  import DealSpendSteeringForm from '@/common/components/deals/form/spendSteering/DealSpendSteeringForm.vue';
  import TargetingSectionHelper, { TargetingSection } from '@/common/components/entity-form/TargetingSectionHelper';
  import EntityChangeList from '@/common/components/EntityChangeList.vue';
  import InfoMessage from '@/common/components/generic-messages/InfoMessage.vue';
  import { Demographics as DemographicTargeting } from '@/common/components/targeting';
  import SideDrawer from '@/common/components/ui/SideDrawer.vue';
  import BudgetModes from '@/common/enums/budget-modes.enum';
  import Environment from '@/common/enums/environment.enum';
  import PurchaseTypes from '@/common/enums/purchase-types.enum';
  import {
    IconColoredAvails,
    IconColoredBudget,
    IconColoredBuyer,
    IconColoredDelivery,
    IconColoredDuration,
    IconColoredMobilePhoneVibrating,
    IconColoredTargeting,
    IconColoredWait,
  } from '@/common/icons/colored';
  import { IconAdSpend, IconImpressions, IconInfo } from '@/common/icons/monochrome';
  import { FilterCollection, OrganizationCustomField, QueryParams, TargetingCriterion } from '@/common/models';
  import DeliveryStatistics from '@/common/models/deliveryStatistics/DeliveryStatistics';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import CuratorOrganization from '@/common/models/misc-api-models/CuratorOrganization';
  import OrganizationDSP from '@/common/models/misc-api-models/OrganizationDSP';
  import Option from '@/common/models/Option';
  import { AuctionType } from '@/common/models/package/AuctionType.enum';
  import { CurationType } from '@/common/models/package/CurationType.enum';
  import Package from '@/common/models/package/Package';
  import PackageBuyerOptions from '@/common/models/package/PackageBuyerOptions';
  import PackageProperty from '@/common/models/package/PackageProperty';
  import PackageType from '@/common/models/package/PackageType.enum';
  import { Pricing } from '@/common/models/package/Pricing.enum';
  import PurchaseTypeOptions from '@/common/models/PurchaseTypeOptions';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { environment, FullDaypart, i, region, Regions, toServerDate, useInjected } from '@/globals';
  import { Breadcrumbs, TabList } from '@/org-app/components/controls';
  import ExperimentalBadge from '@/org-app/components/controls/ExperimentalBadge.vue';
  import CuratedDealEstimatedDeliveryStats from '@/org-app/components/curatorDeals/CuratedDealEstimatedDeliveryStats.vue';
  import EditCadence from '@/org-app/components/lineItems/EditCadence.vue';
  import EstimatedDeliveryStats from '@/org-app/components/lineItems/EstimatedDeliveryStats.vue';
  import OverallSummary from '@/org-app/components/lineItems/OverallSummary.vue';
  import EntityCustomFieldsEditor from '@/org-app/components/organizationCustomFields/EntityCustomFieldsEditor.vue';
  import { useDealComputedProperties } from '@/org-app/services/computedProperties/DealComputedProperties';
  import { useExternalExchanges, useExternalSSPs, useOrganizationDSPs } from '@/org-app/services/tanstack/queries';
  import { useUserOrganizations } from '@/org-app/services/tanstack/queries/userOrganizations.queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import formatNumber from '@/utils/NumberFormatter';
  import { mapActions, mapOrgAppActions, mapOrgAppGetters, mapOrgAppState } from '@/utils/StateMapping';
  import ThrottledTaskRunner from '@/utils/ThrottledTaskRunner';
  import UserNotifications from '@/utils/UserNotifications';
  import MessageCollection from '@/validator/MessageCollection';
  import { IEntityChange } from '@/validator/Validatable';
  import Validator from '@/validator/Validator';
  import i18n from '@/vueI18n';

  import EditDealBuyerOption from './EditDealBuyerOption.vue';

  //#endregion

  //#region Dynamic imports

  const AvailsResult = defineAsyncComponent(() => import('../AvailsResult.vue'));

  //#endregion

  //#region Emits

  const emit = defineEmits<{
    savePackage: [pack: Package, callback: () => void];
    refreshAudienceStats: [targetParams: never, cpmTargetParams: never];
    purchaseTypeChanged: [purchaseType: Option];
    restoreDeal: [restore: boolean];
  }>();

  //#endregion

  //#region Props

  interface IEditDealProps {
    isExchange?: boolean;
    isOrganization?: boolean;
    deliveryStatistics: DeliveryStatistics;
    buyerOrganizationDSPs: OrganizationDSP[];
    externalAvailsUrl: string;
    isAdDurationSectionVisible: boolean;
    isCheckAvailsEnabled: boolean;
    isCustomDemographicsEnabled: boolean;
    isDaypartSectionVisible: boolean;
    isScreenFrequencyCapSectionEnabled: boolean;
    isGeofenceGroupTargetingEnabled: boolean;
    isMobileRetargetingEnabledForUI: boolean;
    isNew: boolean;
    isCloning: boolean;
    isSiteTargetingEnabled: boolean;
    isUnitTargetingEnabled: boolean;
    isWeatherTargetingSectionEnabled: boolean;
    allMediaOwners: MediaOwner[];
    planID?: number;
    organizationID?: number;
    organizationCustomFields?: OrganizationCustomField[];
    exchangeID?: number;
    targetingCriteria: TargetingCriterion[];
    isOrgOnInternalExchange?: boolean;
    showAudienceCPM: boolean;
    forceNetworkSelection: boolean;
    showHideUnitInformationInBidRequests: boolean;
    defaultStateForHidingUnitInformationInBidRequests: boolean;
  }

  const props = withDefaults(defineProps<IEditDealProps>(), {
    isExchange: false,
    isScreenFrequencyCapSectionEnabled: true,
    isOrganization: false,
    isOrgOnInternalExchange: false,
    isWeatherTargetingSectionEnabled: true,
    exchangeID: null,
    planID: null,
    organizationID: null,
    organizationCustomFields: null,
  });

  //#endregion

  //region Inject

  const $validator: Validator = inject('validator');
  const deal: Ref<Package> = useInjected<Package>('deal');

  //endregion

  //#region Getters / State mapping

  const { isDemandOrganization, isSupplyOrganization, isCuratorOrganization } = mapOrgAppGetters();
  const { organizationSettings, ReferenceData } = mapOrgAppState();
  const externalExchangesQuery = useExternalExchanges();
  const externalSSPsQuery = useExternalSSPs(undefined, {
    queryParams: QueryParams.all(FilterCollection.for('curate_enabled', [true])),
  });
  const organizationDSPsQuery = useOrganizationDSPs(); // Used by peekQueryInScope inside Package.ts
  const userOrganizationsQuery = useUserOrganizations(); // Used by peekQueryInScope inside Package.ts

  //#endregion

  //#region Dynamic computed properties types

  const restrictedExternalExchanges = ['DV360', 'Xandr', 'Bell'] as const;
  type RestrictedExternalExchanges = (typeof restrictedExternalExchanges)[number];
  type ExchangeComputedPropertyMap = {
    [exchange in RestrictedExternalExchanges as `allBuyersAre${exchange}`]: ComputedRef<boolean>;
  } & {
    [exchange in RestrictedExternalExchanges as `is${exchange}ExchangeSelected`]: ComputedRef<boolean>;
  };

  //#endregion

  //#region State

  enum DealCreationModes {
    DEFAULT = 'default',
    EXTERNAL = 'external',
  }

  const NOTIFICATIONS_CATEGORY = 'EDIT_DEAL';
  let lastBuyerSearchTerm = null;

  const router = useRouter();

  const isMounted = ref(false);
  const breadcrumbs = ref([
    {
      name: isCuratorOrganization.value
        ? capitalize(i18n.global.t('names.curatedDeal', { n: 2 }))
        : capitalize(i18n.global.t('names.deal', { n: 2 })),
      url: isCuratorOrganization.value ? '/curated-deals/' : '/deals/',
    },
  ]);
  const adDurationTypeInternal = ref('fixed');
  const auctionTypeOptions = ref(AuctionType);
  const audienceDescription = ref(null);
  const audienceImpressionsMultiplier = ref(1);
  const audienceIndex = ref(1);
  const budgetTypeInternal = ref('fixed');
  const checkAvailsAlert = ref(null);
  const colors = ref(Colors);
  const confirmLosingChangesCallback = ref(null);
  const dealChanges: Ref<IEntityChange<Package>[]> = ref([]);
  const demographicError = ref(null);
  const entityTimestamp = ref(DateTime.local());
  const errors: Ref<MessageCollection> = ref(new MessageCollection());
  // Provides validation errors to nested components to avoid prop drilling.
  provide('errors', errors);
  const estimatedAudienceImpressions = ref(null);
  const estimatedScreenCount = ref(null);
  const estimatedSiteCount = ref(null);
  const estimatedTotalImpressions = ref(null);
  const hasCheckedAvails = ref(false);
  const hasFirstLoadedCadence = ref(false);
  const hasStartedFiltering = ref(false);
  const impressionsTypeInternal = ref('fixed');
  const isCheckAvailsUnavailableModalVisible = ref(false);
  const isIncompatibleSelectionModalVisible = ref(false);
  const isCheckingAvails = ref(false);
  const isConfirmLosingChangesModalVisible = ref(false);
  const isInternalDSPSelected = ref(false);
  const isExternalDSPSelected = ref(false);
  const selectedBuyerOption = ref(PackageBuyerOptions.AvailableToAllBuyers);
  const isFlexible = ref(!isCuratorOrganization.value);
  const isMissingSomeSelectedExchange = ref(false);
  const isMissingAllSelectedExchange = ref(false);
  const isReady = ref(false);
  const isSaveChangesModalVisible = ref(false);
  const isXandrWarningModalVisible = ref(false);
  const lastAvailsResult = ref(null);
  const lastDeal = ref(null);
  const loadBuyerOptions = ref(false);
  const minSecondsBetweenPlaysTypeInternal = ref('fixed');
  const numberOfDaysTypeInternal = ref('fixed');
  const onePlayPerHourImpressions = ref(null);
  const onePlayPerHourDemographicImpressions = ref(null);
  const optimizedCadence = ref(null);
  const organizationExternalExchangesInternal = ref([]);
  const pricingTypeInternal = ref(deal.value.isCPMFromRateCard ? Pricing.RateCard : Pricing.Static);
  const pricingTypeOptions = ref(Pricing);
  const proximityGeofences = ref([]);
  const saveChangesConfirmationModalID = ref('edit_package_save_changes_confirmation_modal');
  const selectedCuratorOrganizations = ref([]);
  const selectedBuyerExternalExchanges = ref([]);
  const selectedBuyerOrganizationDSPs = ref([]);
  const shouldRefreshCadence = ref(false);
  const isDealBought = ref(false);
  const lastSelectedBuyerOption = ref(null);
  const originalDemographicTargetsOverridable = ref(true);
  // For NG Internal Deal
  const hasAdDuration = ref(false);
  const hasMinSecondsBetweenPlays = ref(false);
  const xandrWarningMessage = ref(null);
  const targetingSectionHelper = ref(null);
  const selectedAdditionalTargetingCriteria = ref([]);
  const sections = ref([]);
  // For child deals table filters (used in Curated Deals)
  const childDealsFilters = ref(null);
  // For deal preview
  const activeDeal = ref(null);
  const dealCreationMode = ref('');
  const didDetectUnexpectedChanges = ref(false);

  // DOM refs

  const editCadenceRef = ref(null);
  const availsResultRef = ref(null);
  const estimatedDeliveryStatsRef = ref(null);
  const curatedDealEstimatedDeliveryStatsRef = ref(null);

  const editCrossSSPDealRef = useTemplateRef<ComponentExposed<typeof EditCrossSSPDeal>>('editCrossSSPDealRef');
  const dealMapRef = useTemplateRef<ComponentExposed<typeof DealMap>>('dealMapRef');

  //#endregion

  //#region Computed

  const {
    startDate,
    endDate,
    isEvergreenDeal,
    dealID,
    auctionType,
    cadence,
    isForBidding,
    hideUnitInformationInBidRequests,
    numberOfDaysMin,
    numberOfDaysMax,
    numberOfDaysOverridable,
    daypart,
    adDurationMin,
    minConcentrationFactor,
    minConcentrationValue,
    adDurationMax,
    adDurationOverridable,
    enableMobileRetargeting,
    minSecondsBetweenPlaysMin,
    minSecondsBetweenPlaysMax,
    minSecondsBetweenPlaysOverridable,
    impressionsMin,
    impressionsMax,
    impressionsOverridable,
    budgetMin,
    budgetMax,
    budgetOverridable,
    impressionsAndBudgetOverridable,
    locationIDs,
    locationExclusionIDs,
    locationsOverridable,
    siteIDs,
    siteExclusionIDs,
    unitIDs,
    unitExclusionIDs,
    unitPackIDs,
    unitPackExclusionIDs,
    unitTags,
    unitTagExclusions,
    geofenceGroupIDs,
    geofenceGroupExclusionIDs,
    marketIDs,
    demographicIDs,
    demographicsOverridable,
    networkIDs,
    networksOverridable,
    mediaTypeIDs,
    mediaTypesOverridable,
    buyerOrganizationDSPIDs,
    buyerExternalIDs,
    creativeFormats,
    creativeFormatExclusions,
    cpmFixed,
    unitAssignmentsOverridable,
  } = useDealComputedProperties(deal, {
    startDate: () => refreshEstimatedDeliveryStats(),
    endDate: () => refreshEstimatedDeliveryStats(),
    numberOfDaysMin: (value: number) => {
      if (!isReady.value) {
        return;
      }
      if (numberOfDaysType.value === 'fixed') {
        numberOfDaysMax.value = value;
      }
    },
    daypart: () => refreshEstimatedDeliveryStats(),
    adDurationMin: () => {
      if (!isReady.value) {
        return;
      }
      if (adDurationType.value === 'fixed') {
        adDurationMax.value = adDurationMin.value;
      }
      refreshEstimatedDeliveryStats();
    },
    adDurationMax: () => refreshEstimatedDeliveryStats(),
    adDurationOverridable: () => refreshEstimatedDeliveryStats(),
    minSecondsBetweenPlaysMin: () => {
      if (!isReady.value) {
        return;
      }
      if (minSecondsBetweenPlaysType.value === 'fixed') {
        minSecondsBetweenPlaysMax.value = minSecondsBetweenPlaysMin.value;
      }
      refreshEstimatedDeliveryStats();
    },
    minSecondsBetweenPlaysMax: () => refreshEstimatedDeliveryStats(),
    impressionsMin: () => {
      if (!impressionsAndBudgetOverridable.value) {
        impressionsMax.value = impressionsMin.value;
      }
    },
    impressionsMax: () => {
      if (!isReady.value) {
        return;
      }
      if (impressionsAndBudgetOverridable.value === false) {
        budgetMax.value = budgetMin.value;
      }
    },
    demographicIDs: () => refreshEstimatedDeliveryStats(),
    cpmFixed: () => {
      queueRefreshAudienceStats();
      refreshEstimatedDeliveryStats();
    },
  });

  const tabOptions = computed(() => {
    return [
      {
        id: 'default',
        title: capitalize(i18n.global.t('messages.hivestackSSP')),
      },
      {
        id: 'external',
        title: capitalize(i18n.global.t('messages.otherSSP')),
      },
    ];
  });
  // Temporary while we work on the functionality for cross SSP deals (experimental)
  const canShowCrossSSP = computed(() => {
    const isCompatibleOrganization = environment === Environment.PROD ? props.organizationID === 2922 : true;
    return isCuratorOrganization.value && isCompatibleOrganization;
  });
  const allSections = computed(() => targetingSectionHelper.value?.allSections || []);
  const availableSections = computed(() =>
    allSections.value.filter((x) => !selectedAdditionalTargetingCriteria.value.map((s) => s.id).includes(x.id)),
  );
  const curatorOrganizations = computed(
    () => ReferenceData?.curators?.map((curator) => CuratorOrganization.fromApi(curator)) || [],
  );
  const organizationExternalExchanges = computed(() => externalExchangesQuery.items.value);
  const organizationExternalSSPs = computed(() => externalSSPsQuery.items.value);
  const selectedBuyerOptionInternal = computed(() => selectedBuyerOption.value);
  const isNotChina = computed(() => region !== Regions.China);
  const isBeingCreatedFromPlan = computed(() => props.planID != null);
  const isDV360BuyerSelected = computed(
    () =>
      selectedRestrictedBuyerOrganizationDSPs.value.filter((x) => x.dsp_name.toLowerCase().includes('dv360')).length >
      0,
  );
  const isXandrBuyerSelected = computed(
    () =>
      selectedRestrictedBuyerOrganizationDSPs.value.filter((x) => x.dsp_name.toLowerCase().includes('xandr')).length >
      0,
  );
  const isBellBuyerSelected = computed(
    () =>
      selectedRestrictedBuyerOrganizationDSPs.value.filter((x) => x.dsp_name.toLowerCase().includes('bell')).length > 0,
  );
  const showUnitInformationInBidRequests = computed(
    () => props.showHideUnitInformationInBidRequests && (isPGDeal.value || isNGExternalOrPreferredExternalDeal.value),
  );
  // TODO Once demographics are tested for CD, we will remove the environment specific logic (Only allows demos in Hivestack Curate)
  const isDemographicsDisabled = computed(
    () =>
      (environment === Environment.PROD && isCuratorOrganization.value && props.organizationID !== 2922) ||
      selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificCurators,
  );
  const pricingType = computed({
    get() {
      return pricingTypeInternal.value;
    },
    set(value) {
      if (value !== pricingTypeInternal.value) {
        toggleCPMFromRateCard();
      }
    },
  });
  const purchaseType = computed({
    get() {
      return purchaseTypes.value.find((x) => x.value === deal.value.purchaseType);
    },
    set(pt) {
      if (props.isNew && isForBidding.value) {
        isCPMFromRateCard.value = false;
      } else if (pt.value === PurchaseTypes.NON_GUARANTEED || pt.value === PurchaseTypes.PREFERRED) {
        selectedBuyerOption.value =
          organizationExternalExchanges.value.length > 0
            ? PackageBuyerOptions.AvailableToSpecificDsps
            : PackageBuyerOptions.AvailableToSpecificBuyers;
        isCPMFromRateCard.value = false;
      }
      updatePackagePurchaseType({
        package: deal.value,
        value: pt.value,
      });
      if (pt.value === PurchaseTypes.GUARANTEED) {
        isFlexible.value = false;
        if (props.isNew) {
          isCPMFromRateCard.value = false;
          isAdDurationBasedOnScreens.value = false;
        }
      }
      if (pt.value === PurchaseTypes.GUARANTEED || pt.value === PurchaseTypes.CADENCE) {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToAllBuyers;
      }

      // Remove notification in case there are some displayed
      UserNotifications.clear(NOTIFICATIONS_CATEGORY);

      // Cancel avails request when purchase type change
      if (isCheckingAvails.value) {
        if (availsResultRef.value?.cancelGetAvailsResultTokenSource != null) {
          availsResultRef.value.cancelGetAvailsResultTokenSource.cancel();
        }
      }
    },
  });
  const numberOfDaysType = computed({
    get() {
      return numberOfDaysTypeInternal.value;
    },
    set(value) {
      numberOfDaysTypeInternal.value = value;
      if (!isReady.value) {
        return;
      }
      if (value === 'fixed') {
        numberOfDaysMax.value = numberOfDaysMin.value;
      }
    },
  });
  const adDurationType = computed({
    get() {
      return adDurationTypeInternal.value;
    },
    set(value) {
      adDurationTypeInternal.value = value;
      if (!isReady.value) {
        return;
      }
      if (value === 'fixed') {
        adDurationMax.value = adDurationMin.value;
      }
      refreshEstimatedDeliveryStats();
    },
  });
  const minSecondsBetweenPlaysType = computed({
    get() {
      return minSecondsBetweenPlaysTypeInternal.value;
    },
    set(value) {
      minSecondsBetweenPlaysTypeInternal.value = value;
      if (!isReady.value) {
        return;
      }
      if (value === 'fixed') {
        minSecondsBetweenPlaysMax.value = minSecondsBetweenPlaysMin.value;
      }
      refreshEstimatedDeliveryStats();
    },
  });
  const budgetMode = computed({
    get() {
      return deal.value.budgetMode;
    },
    set(value) {
      if (deal.value.budgetMode === value) {
        return;
      }
      if (!isReady.value) {
        return;
      }
      updatePackageBudgetMode({
        package: deal.value,
        value,
      });
    },
  });
  const impressionsType = computed({
    get() {
      return impressionsTypeInternal.value;
    },
    set(value) {
      impressionsTypeInternal.value = value;
      if (!isReady.value) {
        return;
      }
      if (value === 'fixed') {
        impressionsMax.value = impressionsMin.value;
      }
    },
  });
  const estimatedAverageConcentration = computed(() =>
    estimatedTotalImpressions.value === 0 ? 0 : estimatedAudienceImpressions.value / estimatedTotalImpressions.value,
  );
  const budgetType = computed({
    get() {
      return budgetTypeInternal.value;
    },
    set(value) {
      budgetTypeInternal.value = value;
      if (!isReady.value) {
        return;
      }
      if (value === 'fixed') {
        budgetMax.value = budgetMin.value;
      }
    },
  });
  const isCPMFromRateCard = computed({
    get() {
      return deal.value.isCPMFromRateCard;
    },
    set(value) {
      let val = true;
      if (value === true) {
        val = true;
        cpmFixed.value = 0;
        queueRefreshAudienceStats();
        pricingTypeInternal.value = pricingTypeOptions.value.RateCard;
      } else if (value === false) {
        pricingTypeInternal.value = pricingTypeOptions.value.Static;
        val = false;
        cpmFixed.value = cpmFixed.value ?? 0;
      }
      updatePackageIsCPMFromRateCard({
        package: deal.value,
        value: val,
      });
      refreshEstimatedDeliveryStats();
    },
  });
  const mediaOwnerIDs = computed({
    get() {
      if (isSupplyOrganization.value || isDemandOrganization.value) {
        return [props.organizationID];
      }
      return deal.value.mediaOwnerIDs; // Curators and exchanges
    },
    set(value) {
      if (props.isExchange) {
        updatePackageMediaOwnerIDs({
          package: deal.value,
          value,
        });
      } else if (props.isOrganization) {
        updatePackageMediaOwnerIDs({
          package: deal.value,
          value: isCuratorOrganization.value ? value : [props.organizationID],
        });
      }
      refreshEstimatedDeliveryStats();
      activeMapRef.value?.getFilteredLocations();
    },
  });
  const selectedRestrictedBuyerOrganizationDSPs = computed({
    get() {
      return selectedBuyerOrganizationDSPs.value;
    },
    set(value) {
      // Is true when the radio button is toggled on 'All'
      if (value.length === 0) {
        selectedBuyerOrganizationDSPs.value = [];
        buyerOrganizationDSPIDs.value = [];
        buyerExternalIDs.value = [];
        return;
      }
      // Check to see if the latest clicked DSP is internal
      const isLatestDSPInternal = value[value.length - 1].exchange === 'internal';

      // Get all external and internal DSPs from value which contains all selected DSPs
      let internalBuyers = value.filter((x) => x.exchange === 'internal');
      let externalBuyers = isPGandBuyerDefinable.value ? [] : value.filter((x) => x.exchange === 'external');

      /**
       * The following condition apply if deal is pg and predefined
       *
       * 1 - If latest added DSP is external and previously selected DSP was external as well, the new one replaces the old one.
       *
       * 2 - If latest added DSP is external and previously selected DSPs were internal, remove them because we can either have multiple internal or one external.
       *
       * 3 - If latest added DSP is internal, remove all external DSPs if one was already selected.
       */
      if (isPGandPredefined.value) {
        if (!isLatestDSPInternal && selectedBuyerOrganizationDSPs.value.some((x) => x.exchange === 'external')) {
          externalBuyers = [externalBuyers[externalBuyers.length - 1]];
        }
        if (!isLatestDSPInternal && selectedBuyerOrganizationDSPs.value.some((x) => x.exchange === 'internal')) {
          internalBuyers = [];
        }
        if (isLatestDSPInternal && selectedBuyerOrganizationDSPs.value.some((x) => x.exchange === 'external')) {
          externalBuyers = [];
        }
      }
      const buyers = [...internalBuyers, ...externalBuyers];

      if (props.isNew && isNGExternalOrPreferredExternalDeal.value && buyers.length > 1) {
        auctionType.value = auctionTypeOptions.value.FirstPrice;
      }

      /**
       * If external dsp is selected and organization_dsp_id is not available then update the buyerExternalIDs and empty buyerOrganizationDSPIDs
       * Otherwise, populate buyerOrganizationDSPIDs with the supplied organization_dsp_id and empty the buyerExternalIDs
       */
      if (externalBuyers.length > 0 && externalBuyers.some((buyer) => buyer.organization_dsp_id == null)) {
        buyerOrganizationDSPIDs.value = [];
        buyerExternalIDs.value = externalBuyers
          .filter((x) => x.buyer_external_id != null)
          .map((x) => x.buyer_external_id);
      } else {
        isForBidding.value = !!(isNGOrPreferredDeal.value && buyers.every((x) => x.exchange === 'external'));
        buyerOrganizationDSPIDs.value = buyers
          .filter((x) => x.organization_dsp_id != null)
          .map((x) => x.organization_dsp_id);
        buyerExternalIDs.value = [];
      }
      selectedBuyerOrganizationDSPs.value = buyers;
    },
  });
  const selectedRestrictedCuratorOrganizations = computed({
    get() {
      return selectedCuratorOrganizations.value;
    },
    set(value) {
      const curatorIDs = value.map((x) => x.curatorOrganizationID);
      selectedCuratorOrganizations.value = curatorOrganizations.value.filter((x) =>
        curatorIDs.includes(x.curatorOrganizationID),
      );
      updatePackageCuratorOrganizationIDs({
        package: deal.value,
        value: curatorIDs,
      });
    },
  });
  const selectedRestrictedBuyerExternalExchanges = computed({
    get() {
      return selectedBuyerExternalExchanges.value;
    },
    set(value) {
      const ids = value.map((x) => x.externalExchangeID);
      selectedBuyerExternalExchanges.value = organizationExternalExchanges.value.filter((x) =>
        ids.includes(x.externalExchangeID),
      );
      if (
        isInternalDSPSelected.value ||
        isDV360ExchangeSelected.value ||
        isXandrExchangeSelected.value ||
        isBellExchangeSelected.value
      ) {
        return;
      }
      updatePackageBuyerExternalExchangeIDs({
        package: deal.value,
        value: selectedBuyerExternalExchanges.value.map((x) => x.externalExchangeID),
      });
    },
  });

  // Computed properties for Xandr/Bell/DV360
  const restrictedExchangeComputedProperties = restrictedExternalExchanges.reduce((acc, exchange) => {
    const { allBuyersAreExchange, isExchangeSelected } = createExchangeComputedProperties(exchange);
    acc[`allBuyersAre${exchange}`] = allBuyersAreExchange;
    acc[`is${exchange}ExchangeSelected`] = isExchangeSelected;
    return acc;
  }, {} as ExchangeComputedPropertyMap);

  // Destructuring will allow us to use the computed properties directly (maintains reactivity)
  const {
    allBuyersAreDV360,
    isDV360ExchangeSelected,
    allBuyersAreXandr,
    isXandrExchangeSelected,
    allBuyersAreBell,
    isBellExchangeSelected,
  } = restrictedExchangeComputedProperties;

  const isPGDeal = computed(() => purchaseType.value?.value === PurchaseTypes.GUARANTEED);
  const isPGandBuyerDefinable = computed(
    () => isFlexible.value && purchaseType.value.value === PurchaseTypes.GUARANTEED,
  );
  const isPGandPredefined = computed(() => !isFlexible.value && purchaseType.value.value === PurchaseTypes.GUARANTEED);
  const buyerOrganizationDSPsForRestrictedDeals = computed(() =>
    isPGandBuyerDefinable.value
      ? props.buyerOrganizationDSPs.filter((x) => x.organizations.some((o) => o.exchange === 'internal'))
      : props.buyerOrganizationDSPs,
  );
  const listEndpoint = computed(() => {
    if (props.isExchange) {
      return '/exch/deals';
    }
    return '/deals/';
  });
  const detailsEndpoint = computed(() => {
    if (props.isExchange) {
      return `/exch/deals/${deal.value.id}`;
    } else if (deal.value.isCuratedDeal) {
      return `/curate/deals/${deal.value.id}`;
    }
    return `/deals/${deal.value.id}`;
  });
  const no = computed(() => capitalize(i18n.global.t('actions.no')));
  const yes = computed(() => capitalize(i18n.global.t('actions.yes')));
  const saveChangesConfirmationModalTitle = computed(() => {
    const message = props.isNew ? 'saveNewEntity' : 'saveChanges';
    return capitalize(i18n.global.t(`actions.${message}`));
  });
  const saveChangesConfirmationModalBody = computed(() => {
    const message = props.isNew ? 'confirmSaveNewEntity' : 'confirmSaveChanges';
    return capitalize(i18n.global.t(`messages.${message}`));
  });
  const cpmTargetParams = computed(() => {
    let pt = purchaseType.value.value;
    if (isNGExternalDeal.value) {
      pt = PurchaseTypes.NON_GUARANTEED;
    } else if (isPreferredExternalDeal.value) {
      pt = PurchaseTypes.PREFERRED;
    }
    return {
      from_date: toServerDate(startDate.value),
      to_date: toServerDate(endDate.value),
      ad_duration: adDuration.value,
      daypart: daypart.value,
      demographic_ids: demographicIDs.value,
      screen_frequency_cap: minSecondsBetweenPlaysMin.value ?? 0,
      location_ids: locationIDs.value?.length === 0 && siteIDs.value?.length > 0 ? [] : locationIDs.value,
      location_exclusion_ids: locationExclusionIDs.value,
      network_ids: networkIDs.value,
      unit_ids: unitIDs.value,
      unit_exclusion_ids: unitExclusionIDs.value,
      unitpack_ids: unitPackIDs.value,
      unitpack_exclusion_ids: unitPackExclusionIDs.value,
      unit_tags: unitTags.value,
      unit_tag_exclusions: unitTagExclusions.value,
      site_ids: siteIDs.value,
      site_exclusion_ids: siteExclusionIDs.value,
      geofencegroup_ids: geofenceGroupIDs.value,
      geofencegroup_exclusion_ids: geofenceGroupExclusionIDs.value,
      market_ids: marketIDs.value,
      creative_formats: creativeFormats.value,
      creative_format_exclusions: creativeFormatExclusions.value,
      purchase_type: pt,
      owner_organization_ids: mediaOwnerIDs.value,
      buyer_organization_id: props.organizationID,
      buyer_exchange_id: null,
      buyer_dsp_id: null,
      package_id: deal.value.id,
      package_organization_id: props.organizationID,
      package_exchange_id: props.exchangeID,
      lineitem_id: null,
      owner_ids: mediaOwnerIDs.value,
    };
  });
  const targetParams = computed(() => ({
    from_date: toServerDate(startDate.value),
    to_date: toServerDate(endDate.value),
    organization_id: props.organizationID,
    unit_targeting: {
      package_id: null,
      lineitem_id: null,
      purchase_type: purchaseType.value.value,
      organization_id: props.organizationID,
      owner_ids: mediaOwnerIDs.value,
      location_ids: locationIDs.value?.length === 0 && siteIDs.value?.length > 0 ? [] : locationIDs.value,
      location_exclusion_ids: locationExclusionIDs.value,
      geofencegroup_ids: geofenceGroupIDs.value,
      geofencegroup_exclusion_ids: geofenceGroupExclusionIDs.value,
      mediatype_ids: mediaTypeIDs.value,
      mediatype_exclusion_ids: [],
      market_ids: marketIDs.value,
      market_exclusion_ids: [],
      network_ids: networkIDs.value,
      network_exclusion_ids: [],
      site_ids: siteIDs.value,
      site_exclusion_ids: siteExclusionIDs.value,
      unit_ids: unitIDs.value,
      unit_exclusion_ids: unitExclusionIDs.value,
      creative_formats: creativeFormats.value,
      creative_format_exclusions: creativeFormatExclusions.value,
      unit_tags: unitTags.value,
      unit_tag_exclusions: unitTagExclusions.value,
      unitpack_ids: unitPackIDs.value,
      unitpack_exclusion_ids: unitPackExclusionIDs.value,
    },
    daypart: daypart.value || undefined,
    demographic_ids: demographicIDs.value || undefined,
  }));
  const currencySymbol = computed(
    () => deal.value?.currencyInformation?.symbol ?? numbro.languageData().currency.symbol,
  );
  const adDuration = computed(() =>
    canShowAdDuration.value && adDurationMin.value != null ? adDurationMin.value : 10,
  );
  const isCheckingAvailsAvailable = computed(
    () =>
      !isNGExternalOrPreferredExternalDeal.value &&
      !isFlexible.value &&
      DateTime.fromISO(startDate).startOf('day') >= DateTime.local().startOf('day') &&
      props.isCheckAvailsEnabled,
  );
  const purchaseTypes = computed(() =>
    PurchaseTypeOptions.sortOptionsByPriority(
      isBeingCreatedFromPlan.value
        ? PurchaseTypeOptions.planOptions(true)
        : PurchaseTypeOptions.options(true, props.isExchange),
    ),
  );
  const isNGExternalDeal = computed(
    () =>
      purchaseType.value != null &&
      purchaseType.value.value === PurchaseTypes.NON_GUARANTEED &&
      !isNGInternalDeal.value,
  );
  const isGuaranteedDeal = computed(
    () => purchaseType.value != null && purchaseType.value.value === PurchaseTypes.GUARANTEED,
  );
  const isPurchaseTypeCadence = computed(
    () => purchaseType.value != null && purchaseType.value.value === PurchaseTypes.CADENCE,
  );
  const isNGInternalDeal = computed(
    () =>
      purchaseType.value != null && // Hivestack DSP buyers
      purchaseType.value.value === PurchaseTypes.NON_GUARANTEED &&
      ((isInternalDSPSelected.value && selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificDsps) ||
        (selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificBuyers &&
          (selectedBuyerOrganizationDSPs.value.length === 0 ||
            selectedBuyerOrganizationDSPs.value.every((x) => x.exchange === 'internal')))),
  );
  const isPreferredInternalDeal = computed(
    () =>
      purchaseType.value != null &&
      purchaseType.value.value === PurchaseTypes.PREFERRED &&
      ((isInternalDSPSelected.value && selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificDsps) ||
        (selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificBuyers &&
          (selectedBuyerOrganizationDSPs.value.length === 0 ||
            selectedBuyerOrganizationDSPs.value.every((x) => x.exchange === 'internal')))),
  );
  const isPreferredExternalDeal = computed(
    () =>
      purchaseType.value != null &&
      purchaseType.value.value === PurchaseTypes.PREFERRED &&
      !isPreferredInternalDeal.value,
  );
  const isNGExternalOrPreferredExternalDeal = computed(
    () =>
      (isNGExternalDeal.value || isPreferredExternalDeal.value) &&
      dealCreationMode.value !== DealCreationModes.EXTERNAL,
  );
  const isPreferredOrNGInternalDeal = computed(() => isNGInternalDeal.value || isPreferredInternalDeal.value);
  const isNGOrPreferredDeal = computed(
    () =>
      isNGInternalDeal.value ||
      isNGExternalDeal.value ||
      isPreferredInternalDeal.value ||
      isPreferredExternalDeal.value,
  );
  const showDealScope = computed(() => !isNGOrPreferredDeal.value);
  const isAdDurationBasedOnScreens = computed({
    get() {
      return adDurationMin.value == null;
    },
    set(val) {
      if (val) {
        adDurationMin.value = null;
      } else if (deal.value.adDurationMin) {
        adDurationMin.value = deal.value.adDurationMin;
      } else {
        adDurationMin.value = 10;
      }
    },
  });
  const selectedMediaOwners = computed(() => props.allMediaOwners?.filter((x) => mediaOwnerIDs.value.includes(x.id)));
  const activeMediaOwners = computed(() => props.allMediaOwners?.filter((x) => x.isActive));
  const availableMediaOwners = computed(() => {
    if (deal.value.id === -1) {
      return activeMediaOwners.value;
    }
    return props.allMediaOwners?.filter((x) => mediaOwnerIDs.value.find((y) => y === x.id) != null || x.isActive);
  });
  const entityCustomFields = computed(() =>
    props.organizationCustomFields == null || props.organizationCustomFields.length === 0 || deal.value == null
      ? []
      : props.organizationCustomFields.map((x) => {
          const existing = deal.value.packageProperties.find((y) => y.key === x.key);
          return existing ?? PackageProperty.fromApi(x.toApi());
        }),
  );
  const isPackageCustomFieldRequired = computed(
    () => entityCustomFields.value.filter((ecf) => ecf.isRequired).length > 0,
  );
  const hasExternalAvailsUrl = computed(() => !!props.externalAvailsUrl && props.externalAvailsUrl.trim() !== '');
  /**
   * Media owner should not be able to edit purchase type after deal has been bought (CAM-2539)
   * @returns {boolean}
   */
  const canEditPurchaseType = computed(() => !isDealBought.value);

  const canEditAuctionType = computed(
    () => !((isXandrExchangeSelected.value || isBellExchangeSelected.value) && isDealBought.value),
  );

  const canEditCPMFixed = computed(() => canEditAuctionType.value);

  const canEditAudienceTargeting = computed(() =>
    isPreferredOrNGInternalDeal.value && originalDemographicTargetsOverridable.value ? !isDealBought.value : true,
  );
  const canShowAdDuration = computed(() =>
    isPreferredOrNGInternalDeal.value ? hasAdDuration.value : !adDurationOverridable.value,
  );
  const canShowMinSecondsBetweenPlays = computed(() =>
    isPreferredOrNGInternalDeal.value ? hasMinSecondsBetweenPlays.value : !minSecondsBetweenPlaysOverridable.value,
  );
  const canShowBudgetImpressions = computed(() => !isNGOrPreferredDeal.value);
  const budgetModeTabs = computed(() => [
    { id: BudgetModes.BUDGET, name: capitalize(i18n.global.t('names.adSpend', { n: 1 })), icon: IconAdSpend },
    {
      id: BudgetModes.IMPRESSIONS,
      name: capitalize(i18n.global.t('names.impression', { n: 2 })),
      icon: IconImpressions,
    },
  ]);
  const isCopiedDeal = computed(() => props.isNew && props.isCloning);
  const customEvents = computed(() => {
    const externallyPushedTargetCriteria = props.targetingCriteria.filter((x) => x.isUpdatePushedExternally);
    if (deal.value == null || externallyPushedTargetCriteria.length === 0) return [];
    return deal.value.packageTargetingCriteria.filter((x) =>
      externallyPushedTargetCriteria.some((ec) => ec.id === x.targetingCriterionID),
    );
  });
  const mapSuppliers = computed(() => {
    if (deal.value?.ownerIDsFromChildDealsWrapped && deal.value.ownerIDsFromChildDealsWrapped.length > 0) {
      return deal.value.ownerIDsFromChildDealsWrapped;
    } else if (isCuratorOrganization.value) {
      if (childDealsFilters.value && Object.keys(childDealsFilters.value).length > 0) {
        const { currency, countries } = childDealsFilters.value;

        return props.allMediaOwners?.filter(
          (supplier) =>
            isFilteredDataMatching(supplier.countries, countries) &&
            isFilteredDataMatching([supplier.currency], currency),
        );
      }
      return props.allMediaOwners;
    } else {
      return [];
    }
  });
  const isRestrictedExchangeSelected = computed(() => {
    return isXandrExchangeSelected.value || isBellExchangeSelected.value || isDV360ExchangeSelected.value;
  });
  const showDVBuyerWarning = computed(() => {
    return isRestrictedExchangeSelected.value;
  });
  const localMidnightFormatted = computed(() => {
    return DateTime.utc().startOf('day').toLocal().toFormat('HH:mm');
  });
  const entityFeeChanged = computed(() => {
    return dealChanges.value.some((dc) => dc.property.property === 'entity_fees');
  });
  const hasMismatchedPublisherDeals = computed(() => {
    return deal.value.hasMismatchedPublisherDeals;
  });
  const hasIncompatibleRestrictions = computed(() => {
    return deal.value.hasIncompatibleRestrictions;
  });
  const dealLockedScopeType = computed(() => {
    // If no packages are selected, we can determine if the deal is internally/externally locked based on the buyer/exchange restrictions.
    const hasRestrictions =
      deal.value.buyerOrganizationDSPIDs?.length > 0 ||
      deal.value.buyerExternalExchangeIDs?.length > 0 ||
      isInternalDSPSelected.value;
    // If we have a prior selection issue (usually on initialization), we should enforce the restrictions so the user can fix their selection.
    if ((hasIncompatibleRestrictions.value && hasRestrictions) || hasMismatchedPublisherDeals.value) return null;
    const selectedPackage = deal.value.packages?.find((pkg) => pkg.isForBidding != null);
    return getDealScopeType(selectedPackage?.isForBidding ?? (hasRestrictions ? deal.value.isForBidding : null));
  });
  const activeMapRef: ComputedRef<ComponentExposed<typeof DealMap>> = computed(() => {
    return dealCreationMode.value === DealCreationModes.DEFAULT
      ? dealMapRef.value
      : editCrossSSPDealRef.value?.dealMapRef;
  });
  const shouldDisplayFullScreenMap = computed(() => {
    return activeMapRef.value?.shouldDisplayFullScreenMap ?? false;
  });

  //#endregion

  //#region Functions

  const { getIsDealBought } = mapOrgAppActions();
  const {
    updatePackageBudgetMax,
    updatePackageBudgetMin,
    updatePackageBudgetMode,
    updatePackageBuyerExternalExchangeIDs,
    updatePackageCadenceMin,
    updatePackageCadenceMax,
    updatePackageCadenceSmoothCatchup,
    updatePackageDaypart,
    updatePackageImpressionsMax,
    updatePackageImpressionsMin,
    updatePackageIsCPMFromRateCard,
    updatePackageIsOpen,
    updatePackageMediaOwnerIDs,
    updatePackageProperties,
    updatePackagePurchaseType,
    updatePackageTargetingCriteria,
    updatePackageTargetingCriteriaOverridable,
    updatePackageCustomEventTargetingOverridable,
    updatePackageDaypartOverridable,
    updatePackageCuratorOrganizationIDs,
    updatePackageCurationType,
  } = mapActions();

  function onMinConcentrationFactorChange(concentrationValues) {
    minConcentrationFactor.value = concentrationValues.minConcentrationFactor;
    minConcentrationValue.value = concentrationValues.minConcentrationValue;
  }

  function updateUnitIDsAndRefresh(units) {
    unitExclusionIDs.value = units;
    siteIDs.value = [];
    refreshEstimatedDeliveryStats();
    activeMapRef.value?.initCartFromEntity(deal.value);
  }

  function onChildDealsFilterChanged(filters: { currency: Set<string>; countries: Set<string> }) {
    childDealsFilters.value = filters;
    activeMapRef.value?.getFilteredLocations();
  }

  function isFilteredDataMatching<T>(data: T[], filters: { included: Set<T>; excluded: Set<T> }) {
    const hasMatchingValues = (collection: T[], matchingValues: Set<T>) =>
      collection.some((item) => matchingValues.has(item));

    const { included, excluded } = filters;
    const includedMatch = included.size === 0 || hasMatchingValues(data, included);
    const excludedMatch = excluded.size === 0 || !hasMatchingValues(data, excluded);

    return includedMatch && excludedMatch;
  }

  async function save() {
    // Used for peeking, validated here so that it doesn't stall initial load time.
    await waitForPendingQueries([organizationDSPsQuery, userOrganizationsQuery]);
    if (
      deal.value?.isPGDeal &&
      isCPMFromRateCard.value &&
      selectedBuyerOrganizationDSPs.value?.length > 0 &&
      selectedBuyerOrganizationDSPs.value.some((x) => ['Xandr', 'Bell'].includes(x.dsp_name))
    ) {
      isXandrWarningModalVisible.value = true;
      xandrWarningMessage.value = capitalize(i18n.global.t('messages.xandrPGRateCardWarningMessage'));
      return;
    }

    if (
      selectedBuyerOrganizationDSPs.value.some((x) => ['Xandr', 'Bell'].includes(x.dsp_name)) &&
      (deal.value?.isEvergreen ||
        deal.value.endDate > startDate.value.plus({ days: deal.value.MaxXandrDurationInDays }))
    ) {
      isXandrWarningModalVisible.value = true;
      xandrWarningMessage.value = capitalize(i18n.global.t('messages.xandrDealsEndDateWarningMessage'));
      return;
    }

    // Ensure that these fields are null if deal is evergreen
    if (isEvergreenDeal.value) {
      numberOfDaysMax.value = null;
      numberOfDaysMin.value = null;
    }

    // Make sure empty packagetargetingcriterion_values is removed because it enables fees
    removeEmptyPackageTargetingCriteria();

    GlobalEventBus.$emit(GlobalEvents.clearNotifications);

    let extras: Record<string, unknown> = {};

    if (dealCreationMode.value === DealCreationModes.DEFAULT) {
      getExtraValidation(extras);
    } else if (deal.value.mediaOwnerIDs.length === 0) {
      extras.eSSP = {
        message: i18n.global.t('messages.curatedDealMissingExternalSSPs'),
        preEvaluated: true,
      };
    }

    // Deal ID
    if (!props.isNew) {
      extras.dealID = {
        value: dealID.value,
        rules: 'required|min:5|max:50',
      };
    }

    extras = {
      ...extras,
      ...deal.value.getExtraValidation(),
    };

    if (
      deal.value.isCuratedDeal &&
      !deal.value.feeType &&
      cpmFixed.value === 0 &&
      dealCreationMode.value !== DealCreationModes.EXTERNAL
    ) {
      extras.cpmFixed = {
        message: i18n.global.t('messages.advertiserCPMMustBeGreaterThanZero'),
        preEvaluated: true,
      };
    }

    if (isFlexible.value) updateOverridableTargeting();

    errors.value.clear();
    $validator.validateAll(deal.value, extras).then(
      async () => {
        if (scenarioIsValidToSave()) {
          if (entityTimestamp.value.equals(deal.value.timestamp)) {
            exit();
          } else if (deal.value.id === -1) {
            saveChangesModalConfirmed();
          } else {
            dealChanges.value = await deal.value.getChanges();
            isSaveChangesModalVisible.value = true;
          }
        }
      },
      () => {
        const message = `${i18n.global.t('messages.failedToSavePackage')}<br/>${$validator.messageBag.htmlErrorList}`;
        UserNotifications.error({
          title: i18n.global.t('messages.missingRequiredValue'),
          message,
        });
        errors.value = $validator.messageBag.errors;
      },
    );
  }

  function getExtraValidation(extras: Record<string, unknown>) {
    if (!numberOfDaysOverridable.value && isFlexible.value) {
      const delta = endDate.value.diff(startDate.value, 'days').toObject().days + 1;
      if (numberOfDaysType.value === 'fixed') {
        extras.minNumberOfDays = {
          value: numberOfDaysMin.value,
          rules: `required|min_value:1|max_value:${delta}`,
        };
      } else if (numberOfDaysType.value === 'range') {
        extras.maxNumberOfDays = {
          value: numberOfDaysMax.value,
          rules: `required_or:numberOfDaysMin|min_value:1|max_value:${delta}`,
        };
        extras.minNumberOfDays = {
          value: numberOfDaysMin.value,
          rules: `required_or:numberOfDaysMax|min_value:1|max_value:${delta}`,
        };
      }
    }
    if (canShowAdDuration.value) {
      if (adDurationType.value === 'fixed') {
        extras.adDurationMin = {
          value: adDurationMin.value,
          rules: 'numeric:int|min_value:0|max_value:60',
        };
      } else if (adDurationType.value === 'range') {
        extras.adDurationMax = {
          value: adDurationMax.value,
          rules: 'required_or:adDurationMin',
        };
        extras.adDurationMin = {
          value: adDurationMin.value,
          rules: 'required_or:adDurationMax',
        };
      }
    }
    //
    // This is a patch work. BE always saves minSecondsBetweenPlays to true for PMPDeal. HSSP-1003 will fix .value
    //
    if (
      canShowMinSecondsBetweenPlays.value &&
      !isPurchaseTypeCadence.value &&
      !isNGExternalOrPreferredExternalDeal.value
    ) {
      if (minSecondsBetweenPlaysType.value === 'fixed') {
        extras.minSecondsBetweenPlaysMin = {
          value: minSecondsBetweenPlaysMin.value,
          rules: 'numeric:int|required|min_value:0',
        };
      } else if (minSecondsBetweenPlaysType.value === 'range') {
        extras.minSecondsBetweenPlaysMax = {
          value: minSecondsBetweenPlaysMax.value,
          rules: 'numeric:int|required_or:minSecondsBetweenPlaysMin|min_value:0',
        };
        extras.minSecondsBetweenPlaysMin = {
          value: minSecondsBetweenPlaysMin.value,
          rules: 'numeric:int|required_or:minSecondsBetweenPlaysMax|min_value:0',
        };
      } else {
        extras.minSecondsBetweenPlaysMin = {
          value: minSecondsBetweenPlaysMin.value,
          rules: 'numeric:int|min_value:0',
        };
      }
    }
    if (!isCPMFromRateCard.value) {
      extras.cpmFixed = {
        value: cpmFixed.value,
        rules: 'required|numeric:float|min_value:0',
      };
    }
    // Budget / Impressions
    if (budgetMode.value === BudgetModes.BUDGET && canShowBudgetImpressions.value) {
      if (impressionsAndBudgetOverridable.value === false) {
        extras.budget = {
          value: budgetMin.value,
          rules: 'required|min_value: 1',
        };
      } else if (budgetType.value === 'range' || impressionsAndBudgetOverridable.value) {
        extras.maxBudget = {
          value: budgetMax.value,
          rules: `required_or:budgetMin|min_value: ${budgetMin.value}`,
        };
        extras.minBudget = {
          value: budgetMin.value,
          rules: 'required_or:budgetMax|min_value: 1',
        };
      }

      extras.budgetMin = {
        value: budgetMin.value,
        rules: 'numeric:currency|max_value:2000000000',
      };
      extras.budgetMax = {
        value: budgetMax.value,
        rules: 'numeric:currency|max_value:2000000000',
      };
    } else if (budgetMode.value === BudgetModes.IMPRESSIONS && canShowBudgetImpressions.value) {
      if (impressionsAndBudgetOverridable.value === false) {
        extras.impressions = {
          value: impressionsMin.value,
          rules: 'required|min_value: 1',
        };
      } else if (impressionsType.value === 'range' || impressionsAndBudgetOverridable.value) {
        extras.maxMarketTargetedImpressions = {
          value: impressionsMax.value,
          rules: `required_or:marketTargetedImpressionsMin|min_value: ${impressionsMin.value}`,
        };
        extras.minMarketTargetedImpressionsMin = {
          value: impressionsMin.value,
          rules: 'required_or:marketTargetedImpressionsMax|min_value: 1',
        };
      }

      extras.marketTargetedImpressionsMin = {
        value: impressionsMin.value,
        rules: 'numeric:currency|max_value:2000000000',
      };
      extras.marketTargetedImpressionsMax = {
        value: impressionsMax.value,
        rules: 'numeric:currency|max_value:2000000000',
      };
    }

    // Buyers
    if (selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificBuyers) {
      extras.buyerOrganizationDSPs = {
        value: selectedBuyerOrganizationDSPs.value,
        rules: 'not_empty',
      };
      if ((isDV360BuyerSelected.value || isXandrBuyerSelected.value) && deal.value.isEvergreen) {
        const errorMessage = isDV360BuyerSelected.value
          ? capitalize(i18n.global.t('messages.dvBuyerWarningForEvergreenDeals'))
          : capitalize(i18n.global.t('messages.xandrBuyerWarningForEvergreenDeals'));
        extras.buyerOrganizationDSPs = {
          message: errorMessage,
          preEvaluated: true,
        };
      }

      const externalDspCount = selectedBuyerOrganizationDSPs.value.filter((x) => x.buyer_external_id != null).length;
      const internalDspCount = selectedBuyerOrganizationDSPs.value.filter((x) => x.organization_dsp_id != null).length;

      if (isFlexible.value && externalDspCount > 0) {
        extras.buyerOrganizationDSPs = {
          message: i18n.global.t('messages.dealBuyersNoExternalDSPsAllowedOnBuyerDefinableDeals'),
          preEvaluated: true,
        };
      } else if (selectedBuyerOrganizationDSPs.value.length === 0) {
        extras.buyerOrganizationDSPs = {
          value: selectedBuyerOrganizationDSPs.value,
          rules: 'not_empty',
        };
      } else if (externalDspCount > 1) {
        extras.buyerOrganizationDSPs = {
          message: i18n.global.t('messages.dealBuyersOnlyOneExternalDSPAllowed'),
          preEvaluated: true,
        };
      } else if (externalDspCount > 0 && internalDspCount > 0) {
        extras.buyerOrganizationDSPs = {
          message: i18n.global.t('messages.dealBuyersNoMixOfExternalAndInternalDSPs'),
          preEvaluated: true,
        };
      }
    } else if (
      selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificDsps &&
      isExternalDSPSelected.value &&
      selectedBuyerExternalExchanges.value?.length === 0
    ) {
      extras.selectedBuyerExternalExchanges = {
        message: i18n.global.t('messages.dspFieldsMustNotBeEmpty'),
        preEvaluated: true,
      };
    } else if (
      selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificDsps &&
      (selectedBuyerExternalExchanges.value?.length === 0 ||
        isXandrExchangeSelected.value ||
        isDV360ExchangeSelected.value ||
        isBellExchangeSelected.value) &&
      selectedBuyerOrganizationDSPs.value.length === 0
    ) {
      extras.buyerOrganizationDSPs = {
        value: selectedBuyerOrganizationDSPs.value,
        rules: 'not_empty',
      };
    } else if (
      isNGExternalOrPreferredExternalDeal.value &&
      organizationExternalExchanges.value.length === 0 &&
      deal.value.isOpen
    ) {
      extras.buyerOrganizationDSPs = {
        message: capitalize(i18n.global.t('messages.cannotCreateAOpenDealWhenThereIsNoExchange')),
        preEvaluated: true,
      };
    } else if (
      selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificDsps &&
      isDV360BuyerSelected.value
    ) {
      if (isNGOrPreferredDeal.value && deal.value.isEvergreen) {
        extras.buyerOrganizationDSPs = {
          message: capitalize(i18n.global.t('messages.dvBuyerWarningForEvergreenDeals')),
          preEvaluated: true,
        };
      }
    } else if (
      selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificCurators &&
      selectedRestrictedCuratorOrganizations.value.length === 0
    ) {
      extras.selectedRestrictedCuratorOrganizations = {
        message: capitalize(i18n.global.t('messages.curatorMustNotBeEmpty')),
        preEvaluated: true,
      };
    }

    // For Existing Deals set isOpen to false and clear external exchange IDs
    if (
      selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificDsps &&
      (isXandrExchangeSelected.value || isDV360ExchangeSelected.value || isBellExchangeSelected.value) &&
      selectedBuyerOrganizationDSPs.value.length > 0
    ) {
      updatePackageIsOpen({
        package: deal.value,
        value: false,
      });
      updatePackageBuyerExternalExchangeIDs({
        package: deal.value,
        value: [],
      });
    }

    //
    // Make sure at least one network is selected whenever the setting to force its selection is on.
    //
    if (!networksOverridable.value && props.forceNetworkSelection && networkIDs.value.length === 0) {
      extras.networkIDs = {
        message: i18n.global.t('messages.youMustSelectAMininumOfOneNetwork'),
        preEvaluated: true,
      };
    }

    if (isPurchaseTypeCadence.value) {
      extras.cadence = {
        value: deal.value.cadenceMin,
        rules: 'required|numeric:float|min_value:0.01',
      };
    }

    if (isPackageCustomFieldRequired.value) {
      entityCustomFields.value.forEach((x) => {
        if (x.isRequired && x.value == null) {
          extras[x.key] = {
            value: x.value,
            rules: 'required|not_empty_or_whitespace',
          };
        }
      });
    }

    if (demographicError.value !== null) {
      extras.demographic = {
        message: demographicError.value,
        preEvaluated: true,
      };
    }
  }

  function focusBuyerMultiselect(buyer?: string) {
    setTimeout(() => {
      const multiselect = window.document.getElementById('deal_buyer_restrictions');
      if (!multiselect) {
        return;
      }
      const inputField = multiselect.getElementsByTagName('input');
      if (!inputField || inputField.length == 0) {
        return;
      }
      inputField[0].focus();
      if (buyer) {
        scrollToBuyerGroup(buyer, multiselect);
      }
    }, 300);
  }

  function scrollToBuyerGroup(buyer: string, multiselect: HTMLElement) {
    const observer = new MutationObserver(() => {
      const multiSelectContent = multiselect.querySelector('.multiselect__content-wrapper');
      const allBuyerGroups = Array.from(multiSelectContent.getElementsByClassName('multiselect__option--group'));
      const selectedBuyerGroup = allBuyerGroups.find((group) =>
        group?.innerText.toLowerCase().includes(buyer.toLowerCase()),
      );
      if (selectedBuyerGroup) {
        const buyerGroupOffset = selectedBuyerGroup.offsetTop;
        multiSelectContent.scrollTo({ top: buyerGroupOffset });
        observer.disconnect();
      }
    });
    observer.observe(multiselect, { childList: true, subtree: true });
    setTimeout(() => {
      observer.disconnect();
    }, 3000);
  }

  function cancel() {
    exit();
  }

  function exit() {
    if (!props.isNew) {
      router.push(detailsEndpoint.value).catch(() => undefined);
    } else if (isBeingCreatedFromPlan.value) {
      router.push(`/plans/${props.planID}`).catch(() => undefined);
    } else {
      router.push(listEndpoint.value).catch(() => undefined);
    }
  }

  function scenarioIsValidToSave() {
    if (isPurchaseTypeCadence.value && !isCheckingAvailsAvailable.value) {
      isCheckAvailsUnavailableModalVisible.value = true;
      return false;
    }

    if (isPurchaseTypeCadence.value && !editCadenceRef.value.scenarioIsValidToSave()) return false;

    if (
      isGuaranteedDeal.value &&
      isCheckingAvailsAvailable.value &&
      !hasCheckedAvails.value &&
      hasExternalAvailsUrl.value
    ) {
      hasCheckedAvails.value = true;
      UserNotifications.warning({
        title: capitalize(i18n.global.t('actions.checkAvails')),
        message: i18n.global.t('messages.checkLatestGuaranteedDealAvails'),
        category: 'EDIT_DEAL',
        actions: [
          {
            label: capitalize(i18n.global.t('actions.iUnderstand')),
            isDismissingNotification: true,
            fn: null,
          },
        ],
        duration: 20000,
      });
      return false;
    }

    // Don't save if check avails still loads
    return !isCheckingAvails.value;
  }

  function clearAudienceTargeting() {
    updatePackageProperties({
      package: deal.value,
      value: deal.value.packageProperties.filter((x) => x.key !== 'beta-audience'),
    });
    demographicIDs.value = [];
    audienceDescription.value = null;
  }

  function saveChangesModalConfirmed() {
    if (isPreferredOrNGInternalDeal.value) {
      let isPackagePropertiesEmpty = true;
      const packageProperties = deal.value.packageProperties.find((x) => x.key === 'beta-audience');
      if (packageProperties) {
        isPackagePropertiesEmpty = JSON.parse(packageProperties.value).selected.length === 0;
        if (isPackagePropertiesEmpty) {
          // Remove the selected option if there's not selected inside.
          updatePackageProperties({
            package: deal.value,
            value: deal.value.packageProperties.filter((x) => x.key !== 'beta-audience'),
          });
        }
      }
      demographicsOverridable.value = demographicIDs.value.length === 0 && isPackagePropertiesEmpty;
    }
    emit('savePackage', deal.value, () => {
      entityTimestamp.value = deal.value.timestamp;
    });
  }

  function saveChangesModalCancelled() {
    isSaveChangesModalVisible.value = false;
  }

  function setupSelectedBuyerOrganizationDSPs() {
    const buyerOrganizationDSPs = props.buyerOrganizationDSPs.map((x) => x.organizations);
    selectedBuyerOrganizationDSPs.value = buyerOrganizationDSPs
      .flat()
      .filter((x) => deal.value.buyerOrganizationDSPIDs?.find((y) => y === x.id) != null);
  }

  function onSelectedRestrictedCuratorOrganizationsChanged(val) {
    selectedRestrictedCuratorOrganizations.value = val;

    if (selectedRestrictedCuratorOrganizations.value.length > 0) {
      isForBidding.value = null;
    }
  }

  function onSelectedRestrictedBuyerOrganizationDSPsChanged(val) {
    selectedRestrictedBuyerOrganizationDSPs.value = val;

    if (isPreferredOrNGInternalDeal.value) {
      isFlexible.value = !isCuratorOrganization.value;
      initializeSettingsForNGOrPreferredInternalDeal(false);
      if (props.isNew && !isCopiedDeal.value && !deal.value.isCuratedDeal) {
        isCPMFromRateCard.value = true;
      }
    }
    if (isNGExternalOrPreferredExternalDeal.value) {
      if (!props.isNew) return;
      initializeSettingsForNGOrPreferredExternalDeal();
      if (!deal.value.isCuratedDeal) {
        isCPMFromRateCard.value = false;
      }
      auctionType.value =
        selectedBuyerOrganizationDSPs.value.length > 1 ? AuctionType.FirstPrice : AuctionType.FixedPrice;
    } else {
      minConcentrationFactor.value = 1;
    }
  }

  function onSelectedBuyerExternalExchangesChanged(val) {
    isInternalDSPSelected.value = val.length > 0 ? val.every((x) => x.code === 'INTERNAL') : false;
    isExternalDSPSelected.value =
      val.length > 0
        ? val.every(
            (x) =>
              x.code !== 'INTERNAL' &&
              !x.code.includes('DV360') &&
              !x.code.includes('XANDR') &&
              !x.code.toLowerCase().includes('bell'),
          )
        : false;
    selectedRestrictedBuyerExternalExchanges.value = val;
  }

  function updateMinStartDate(date) {
    startDate.value = DateTime.fromISO(date);
  }

  function updateMaxEndDate(date) {
    endDate.value = DateTime.fromISO(date);
  }

  const refreshAudienceStatsThrottled = new ThrottledTaskRunner(() =>
    emit('refreshAudienceStats', targetParams.value as never, cpmTargetParams.value as never),
  );

  function queueRefreshAudienceStats() {
    if (
      mediaOwnerIDs.value.length === 0 ||
      startDate.value == null ||
      endDate.value == null ||
      startDate.value > endDate.value
    ) {
      return;
    }

    refreshAudienceStatsThrottled.run();
  }

  function toggleImpressionsAndBudget() {
    impressionsAndBudgetOverridable.value = !impressionsAndBudgetOverridable.value;
  }

  function toggleCPMFromRateCard() {
    isCPMFromRateCard.value = !isCPMFromRateCard.value;
  }

  function toggleAdDuration() {
    if (isPreferredOrNGInternalDeal.value) {
      hasAdDuration.value = !hasAdDuration.value;
      if (!hasAdDuration.value) {
        adDurationMin.value = null;
        adDurationMax.value = null;
      }
    } else {
      adDurationOverridable.value = !adDurationOverridable.value;
    }
  }

  function toggleMinSecondsBetweenPlays() {
    if (isPreferredOrNGInternalDeal.value) {
      hasMinSecondsBetweenPlays.value = !hasMinSecondsBetweenPlays.value;
      if (!hasMinSecondsBetweenPlays.value) {
        minSecondsBetweenPlaysMin.value = null;
        minSecondsBetweenPlaysMax.value = null;
      }
    } else {
      minSecondsBetweenPlaysOverridable.value = !minSecondsBetweenPlaysOverridable.value;
    }
  }

  function toggleDemographics() {
    demographicsOverridable.value = !demographicsOverridable.value;
  }

  function handleMediaOwnersSelectionChanged(mediaOwners) {
    if (Array.isArray(mediaOwners)) {
      mediaOwnerIDs.value = mediaOwners.map((mo) => mo.id);
    }
  }

  async function checkAvails() {
    GlobalEventBus.$emit(GlobalEvents.clearTooltips);
    if (startDate.value < DateTime.local().startOf('day')) {
      checkAvailsAlert.value = i18n.global.t('messages.dealCheckAvailsNotAvailableBecauseStartDateIsInThePast');
      return;
    }
    if (
      (budgetMode.value === BudgetModes.BUDGET && !budgetMin.value) ||
      (budgetMode.value === BudgetModes.IMPRESSIONS && !impressionsMin.value && !impressionsMax.value)
    ) {
      checkAvailsAlert.value = i18n.global.t('messages.dealCheckAvailsNotAvailableBecauseNoGoalsHaveBeenSpecified');
      UserNotifications.error({
        title: i18n.global.t('actions.checkAvails'),
        message: checkAvailsAlert.value,
      });
      return;
    }
    if (!hasExternalAvailsUrl.value || !isCheckingAvailsAvailable.value) {
      isCheckAvailsUnavailableModalVisible.value = true;
      return;
    }
    const checkForOptimizedImpressions = deal.value.cadenceSmoothCatchup && editCadenceRef.value?.isCustomCadence;
    checkAvailsAlert.value = null;
    hasCheckedAvails.value = true;
    if (availsResultRef.value != null) {
      UserNotifications.clear(NOTIFICATIONS_CATEGORY);
      await new Promise((resolve) => {
        setTimeout(async () => {
          await availsResultRef.value?.checkAvails(true, checkForOptimizedImpressions);
          resolve(true);
        }, 100);
      });
    }
  }

  function handlePackageCustomPropertySearchChange(prop, term) {
    prop.search(term);
  }

  async function handlePackageCustomPropertyChange(prop, value) {
    if (value) {
      const existing = deal.value.packageProperties.find((x) => x.key === prop.key);
      if (existing) {
        if (existing.value === value.id) {
          return;
        }
        updatePackageProperties({
          package: deal.value,
          value: deal.value.packageProperties.filter((x) => x.key !== prop.key && x.key !== `${prop.key}-name`),
        });
      }
      updatePackageProperties({
        package: deal.value,
        value: deal.value.packageProperties.concat([
          new PackageProperty(null, null, prop.key, value.id),
          new PackageProperty(null, null, `${prop.key}-name`, value.name),
        ]),
      });
    } else {
      // Remove from list
      updatePackageProperties({
        package: deal.value,
        value: deal.value.packageProperties.filter((x) => x.key !== prop.key && x.key !== `${prop.key}-name`),
      });
    }
  }

  function onOverallMapSelectionsChanged(overallMapTargetingKey: number) {
    refreshEstimatedDeliveryStats();
    if (
      isPurchaseTypeCadence.value &&
      (optimizedCadence.value != null || editCadenceRef.value?.isCustomCadence) &&
      (overallMapTargetingKey > 1 || (!deal.value.hasInventoryTargeting && overallMapTargetingKey === 1))
    ) {
      hasFirstLoadedCadence.value = true;
    }
  }

  function onStatsChanged({ cpmInfo, deliveryStatistics }) {
    if (cpmInfo != null) {
      deal.value.cpmCalculator.calculateCPMFromRateCard(cpmInfo, deliveryStatistics);
    }

    audienceImpressionsMultiplier.value = 1;

    if (deliveryStatistics != null) {
      estimatedTotalImpressions.value = deliveryStatistics.impressions;
      estimatedAudienceImpressions.value =
        deliveryStatistics.demographicImpressions * audienceImpressionsMultiplier.value;
      estimatedSiteCount.value = deliveryStatistics.siteCount;
      estimatedScreenCount.value = deliveryStatistics.screenCount;
      onePlayPerHourImpressions.value = deliveryStatistics.onePlayPerHourImpressions;
      onePlayPerHourDemographicImpressions.value = deliveryStatistics.onePlayPerHourDemographicImpressions;
    }
  }

  function onDemographicChanged(change) {
    audienceDescription.value = change.desc;
    refreshEstimatedDeliveryStats();
  }

  function onDemographicError(error) {
    demographicError.value = error;
  }

  function refreshEstimatedDeliveryStats() {
    if (isCuratorOrganization.value) {
      curatedDealEstimatedDeliveryStatsRef.value?.startLoading();
      queueRefreshAudienceStats();
      activeMapRef.value?.getFilteredLocations();
    } else {
      estimatedDeliveryStatsRef.value?.refresh();
    }
  }

  function onWindowMouseWheel(event) {
    const scroller = window.document.querySelector('#edit_package .v-scroll');
    if (scroller != null) {
      let targetContainer = window.document.querySelector('.estimated-delivery-stats-container');
      if (targetContainer != null && (event.target === targetContainer || targetContainer.contains(event.target))) {
        scroller.scrollTop += event.deltaY;
      }
      targetContainer = window.document.querySelector('.full-screen');
      if (targetContainer != null && (event.target === targetContainer || targetContainer.contains(event.target))) {
        scroller.scrollTop += event.deltaY;
      }
    }
  }

  function onCustomFieldsChanged(customFields) {
    updatePackageProperties({
      package: deal.value,
      value: [
        ...deal.value.packageProperties.filter((x) => customFields.find((y) => x.key === y.key) == null),
        ...customFields.filter((x) => x.value),
      ],
    });
  }

  function onPurchaseTypeChanged(val) {
    if (val[0]?.value === PurchaseTypes.NON_GUARANTEED || val[0]?.value === PurchaseTypes.PREFERRED) {
      if (props.isNew) {
        selectedBuyerOption.value =
          organizationExternalExchanges.value?.length > 0
            ? PackageBuyerOptions.AvailableToSpecificDsps
            : PackageBuyerOptions.AvailableToSpecificBuyers;
      } else if (isPreferredOrNGInternalDeal.value) {
        initializeSettingsForNGOrPreferredInternalDeal();
      }
    }
    if (props.isNew && (val[0]?.value === PurchaseTypes.GUARANTEED || val[0]?.value === PurchaseTypes.CADENCE)) {
      selectedBuyerOption.value = PackageBuyerOptions.AvailableToAllBuyers;
    }
    [purchaseType.value] = val;

    lastBuyerSearchTerm = null;

    if (isPurchaseTypeCadence.value) {
      // Clear Targeting / Triggers
      updatePackageTargetingCriteria({
        package: deal.value,
        value: [],
      });

      // Clear Weather
      selectedAdditionalTargetingCriteria.value = targetingSectionHelper.value.clearWeather(
        selectedAdditionalTargetingCriteria.value,
      );

      // Clear Pollen
      selectedAdditionalTargetingCriteria.value = targetingSectionHelper.value.clearPollen(
        selectedAdditionalTargetingCriteria.value,
      );

      // Clear UV
      selectedAdditionalTargetingCriteria.value = targetingSectionHelper.value.clearUv(
        selectedAdditionalTargetingCriteria.value,
      );

      refreshEstimatedDeliveryStats();
    }
    emit('purchaseTypeChanged', purchaseType.value);
  }

  function initializeSettingsForNGOrPreferredInternalDeal(isMapLoaded = true) {
    isFlexible.value = !isCuratorOrganization.value;
    if (props.isNew && !props.isCloning) {
      adDurationMin.value = isBeingCreatedFromPlan.value ? deal.value.adDurationMin : null;
      adDurationMax.value = null;
      impressionsAndBudgetOverridable.value = true;
      isCPMFromRateCard.value = deal.value.isCPMFromRateCard;
      adDurationOverridable.value = isBeingCreatedFromPlan.value ? deal.value.adDurationMin === 0 : true;
      minSecondsBetweenPlaysOverridable.value = true;
      demographicsOverridable.value = isBeingCreatedFromPlan.value
        ? deal.value.demographicTargetIDs?.length === 0
        : true;
    }
    if (isMapLoaded || isCopiedDeal.value) {
      activeMapRef.value?.setOverridableFields(false);
    }
  }

  function initializeSettingsForNGOrPreferredExternalDeal() {
    isFlexible.value = false;
    budgetMin.value = 0;
    budgetMax.value = 0;
    impressionsMin.value = 0;
    impressionsMax.value = 0;
    minConcentrationFactor.value = 1;
  }

  function onSelectedTargetingSectionsChanged(updatedSections) {
    selectedAdditionalTargetingCriteria.value = [...selectedAdditionalTargetingCriteria.value, ...updatedSections];
    const newSections = selectedAdditionalTargetingCriteria.value.filter((x) => sections.value.indexOf(x) === -1);
    sections.value = [...sections.value, ...newSections];
    setTimeout(() => {
      const scroller = window.document.querySelector('#edit_line_item .form-horizontal.v-scroll');
      if (scroller != null) {
        scroller.scrollTo(0, scroller.scrollHeight);
      }
    }, 100);
  }

  function updateAllSections() {
    selectedAdditionalTargetingCriteria.value = targetingSectionHelper.value.updateAllSections(
      deal.value.packageTargetingCriteria,
      availableMediaOwners.value,
    );
  }

  function onSectionValueUpdated(section, value) {
    switch (section.id) {
      case TargetingSection.Daypart:
        onDaypartUpdated(value);
        break;
      case TargetingSection.ExchangeFeeOverride:
        refreshEstimatedDeliveryStats();
        break;
      case TargetingSection.MediaOwners:
        break;
      default:
        break;
    }
  }

  function onSectionCleared(section) {
    selectedAdditionalTargetingCriteria.value.splice(selectedAdditionalTargetingCriteria.value.indexOf(section), 1);
  }

  function setDefaultValueForHideUnitInformationFromBidRequests() {
    //
    // Clear value for hideUnitInformationInBidRequests when purchase type is Non-guaranteed or House ad (HV-680)
    //
    switch (purchaseType.value?.value) {
      case PurchaseTypes.GUARANTEED:
        hideUnitInformationInBidRequests.value = props.defaultStateForHidingUnitInformationInBidRequests;
        break;
      case PurchaseTypes.NON_GUARANTEED:
      case PurchaseTypes.PREFERRED:
        hideUnitInformationInBidRequests.value = isForBidding.value
          ? props.defaultStateForHidingUnitInformationInBidRequests
          : false;
        break;
      default:
        hideUnitInformationInBidRequests.value = false;
        break;
    }
  }

  function onDaypartUpdated(dp) {
    updatePackageDaypart({
      package: deal.value,
      value: dp,
    });
    refreshEstimatedDeliveryStats();
  }

  function refreshAvails() {
    if (deal.value?.purchaseType !== PurchaseTypes.CADENCE) {
      checkAvails();
    }
  }

  function onCadenceChanged() {
    if (isCheckingAvails.value) availsResultRef.value.cancelCheckAvails();
  }

  function onAvailsResultChanged(result) {
    lastAvailsResult.value = result;
  }

  function removeEmptyPackageTargetingCriteria() {
    const hasEmptyPackageTargetingCriteria = deal.value.packageTargetingCriteria?.some((x) => x.values?.length === 0);

    if (hasEmptyPackageTargetingCriteria) {
      const otherTargetingCriterion = deal.value.packageTargetingCriteria.filter((x) => x.values?.length > 0);
      updatePackageTargetingCriteria({
        package: deal.value,
        value: otherTargetingCriterion,
      });

      // If there is no more targeting criteria, set packageTargetingCriteriaOverridable to true
      if (deal.value.packageTargetingCriteria?.length === 0) {
        updatePackageTargetingCriteriaOverridable({
          package: deal.value,
          value: true,
        });
      }

      if (customEvents.value.length === 0) {
        updatePackageCustomEventTargetingOverridable({
          package: deal.value,
          value: true,
        });
      }
    }
  }

  function updateOverridableTargeting() {
    // When custom events are predefined, event targeting overridable is set to false and targeting criteria overridable remains true.
    const triggers = deal.value.packageTargetingCriteria.filter((value) => !customEvents.value.includes(value));

    updatePackageCustomEventTargetingOverridable({
      package: deal.value,
      value: customEvents.value.length === 0,
    });

    updatePackageDaypartOverridable({
      package: deal.value,
      value: deal.value.daypart === FullDaypart,
    });

    updatePackageTargetingCriteriaOverridable({
      package: deal.value,
      value: triggers.length === 0,
    });
  }

  function createExchangeComputedProperties(exchange: string): ExchangeComputedPropertyMap {
    const allBuyersAreExchange = computed(() => {
      return (
        selectedRestrictedBuyerOrganizationDSPs.value.length > 0 &&
        selectedRestrictedBuyerOrganizationDSPs.value.every((x) =>
          x.dsp_name.toLowerCase().includes(exchange.toLowerCase()),
        )
      );
    });
    const isExchangeSelected = computed(() => {
      if (selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificCurators) {
        return false;
      }
      const isSelected =
        selectedRestrictedBuyerExternalExchanges.value.length > 0 &&
        selectedRestrictedBuyerExternalExchanges.value.some((x) =>
          x.name.toLowerCase().includes(exchange.toLowerCase()),
        );
      return allBuyersAreExchange.value || isSelected;
    });
    return { allBuyersAreExchange, isExchangeSelected };
  }

  // Watcher setup for Xandr/Bell/DV360
  function setupRestrictedExchangesWatcher() {
    restrictedExternalExchanges.forEach((exchangeName) => {
      // Uses the dynamically generated computed properties for each exchange
      const allBuyersAreExchange = restrictedExchangeComputedProperties[`allBuyersAre${exchangeName}`];
      const isExchangeSelected = restrictedExchangeComputedProperties[`is${exchangeName}ExchangeSelected`];

      // Watcher for when the exchange itself is selected
      watch(
        () => isExchangeSelected?.value,
        (val) => {
          if (!val || selectedBuyerOption.value === PackageBuyerOptions.AvailableToSpecificBuyers) return;
          selectedBuyerOption.value = PackageBuyerOptions.AvailableToSpecificBuyers;
          // Will focus the multiselect and scroll to the selected exchange in the dropdown.
          focusBuyerMultiselect(exchangeName);
        },
      );

      // Watcher to handle the cases when all buyers are that of one of the restricted external exchanges.
      watch(
        () => allBuyersAreExchange?.value,
        (val) => {
          if (
            val &&
            !selectedRestrictedBuyerExternalExchanges.value.some((x) =>
              x.name.toLowerCase().includes(exchangeName.toLowerCase()),
            )
          ) {
            const exchange = organizationExternalExchanges.value.find((x) =>
              x.name.toLowerCase().includes(exchangeName.toLowerCase()),
            );
            selectedRestrictedBuyerExternalExchanges.value = [exchange];
          }
        },
      );
    });
  }

  setupRestrictedExchangesWatcher();

  watch(
    () => isReady.value,
    (val) => {
      if (val) {
        updateAllSections();
      }
    },
  );
  watch(
    () => selectedRestrictedBuyerOrganizationDSPs.value,
    (val) => {
      if (isRestrictedExchangeSelected.value && selectedRestrictedBuyerExternalExchanges.value.length > 0) {
        const selectedExchange = selectedRestrictedBuyerExternalExchanges.value[0].name;
        const isEveryBuyerMatchingExchange = val.every((dsp) => {
          return dsp.name.toLowerCase().includes(selectedExchange.toLowerCase());
        });
        if (!isEveryBuyerMatchingExchange) {
          selectedRestrictedBuyerExternalExchanges.value = [];
        }
      }
    },
  );
  watch(
    () => deal.value,
    () => {
      const currentTargetParams = JSON.stringify(deal.value?.cpmTargetParams);
      const previousTargetParams = JSON.stringify(lastDeal.value?.cpmTargetParams);

      let didTargetingChange = currentTargetParams.localeCompare(previousTargetParams) !== 0;

      didTargetingChange = didTargetingChange || !startDate.value?.equals(lastDeal.value?.startDate);
      didTargetingChange = didTargetingChange || !deal.value?.endDate?.equals(lastDeal.value?.endDate);
      didTargetingChange = didTargetingChange || deal.value?.budgetMin !== lastDeal.value?.budgetMin;
      didTargetingChange = didTargetingChange || deal.value?.budgetMax !== lastDeal.value?.budgetMax;
      didTargetingChange =
        didTargetingChange || deal.value?.marketTargetedImpressionsMin !== lastDeal.value?.marketTargetedImpressionsMin;
      didTargetingChange =
        didTargetingChange || deal.value?.marketTargetedImpressionsMax !== lastDeal.value?.marketTargetedImpressionsMax;
      didTargetingChange = didTargetingChange || deal.value?.budgetMode !== lastDeal.value?.budgetMode;

      if (didTargetingChange) {
        // when PG deal and user makes a change, notify to check avails on save
        if (isCheckingAvailsAvailable.value) {
          hasCheckedAvails.value = false;
        }

        lastDeal.value = deal.value.clone();
      }
    },
    { deep: true },
  );
  watch(
    () => purchaseType.value,
    () => {
      if (isNGExternalOrPreferredExternalDeal.value) {
        initializeSettingsForNGOrPreferredExternalDeal();
      } else {
        minConcentrationFactor.value = 1;
      }
      if (!props.isNew) {
        setDefaultValueForHideUnitInformationFromBidRequests();
      }
    },
  );
  watch(
    () => isFlexible.value,
    (val) => {
      activeMapRef.value?.clearFilters();
      //
      // Adjust date range according to flexibility.
      //
      updateMinStartDate(startDate.value);
      updateMaxEndDate(endDate.value);

      adDurationOverridable.value = val;
      adDurationType.value = val ? adDurationType.value : 'fixed';
      numberOfDaysOverridable.value = val;
      minSecondsBetweenPlaysOverridable.value = val;
      minSecondsBetweenPlaysType.value = val ? minSecondsBetweenPlaysType.value : 'fixed';
      demographicsOverridable.value = val;
      budgetOverridable.value = val;
      impressionsOverridable.value = val;

      if (!isPreferredOrNGInternalDeal.value) {
        activeMapRef.value?.setOverridableFields(val);
      }

      if (isPGandBuyerDefinable.value) {
        selectedRestrictedBuyerOrganizationDSPs.value = selectedRestrictedBuyerOrganizationDSPs.value.filter(
          (dsp) => dsp.exchange !== 'external',
        );
      }

      budgetType.value = 'fixed';
      impressionsType.value = 'fixed';
      numberOfDaysMin.value = endDate.value.diff(startDate.value, 'days').toObject().days;
      numberOfDaysMax.value = endDate.value.diff(startDate.value, 'days').toObject().days;
      if (isPurchaseTypeCadence.value && !isCheckingAvailsAvailable.value && val) {
        isCheckAvailsUnavailableModalVisible.value = true;
        return;
      }

      audienceIndex.value += 1;

      if (!val) {
        activeMapRef.value?.initCartFromEntity(deal.value, false);
      }
    },
  );
  watch(
    () => isInternalDSPSelected.value,
    (val) => {
      if (val) {
        initializeSettingsForNGOrPreferredInternalDeal();
        isForBidding.value = false;
        selectedRestrictedBuyerOrganizationDSPs.value = [];
        buyerOrganizationDSPIDs.value = [];
        buyerExternalIDs.value = [];
        if (props.isNew && !isCopiedDeal.value && !deal.value.isCuratedDeal) {
          isCPMFromRateCard.value = true;
          cpmFixed.value = 0;
        } else {
          isCPMFromRateCard.value = deal.value.isCPMFromRateCard;
        }
        minConcentrationFactor.value = 0;
        updatePackageIsOpen({
          package: deal.value,
          value: false,
        });
      } else {
        initializeSettingsForNGOrPreferredExternalDeal();
        isForBidding.value = true;
        if (props.isNew && !deal.value.isCuratedDeal) {
          isCPMFromRateCard.value = false;
        }
      }
    },
  );
  watch(
    () => isExternalDSPSelected.value,
    (val) => {
      if (val) {
        initializeSettingsForNGOrPreferredExternalDeal();
        isForBidding.value = true;
        if (props.isNew && !props.isCloning) {
          auctionType.value = auctionTypeOptions.value.FIRSTPRICE;
        }
        updatePackageIsOpen({
          package: deal.value,
          value: true,
        });
      }
    },
  );
  watch(
    () => startDate.value,
    (val) => {
      numberOfDaysMin.value = endDate.value.diff(val, 'days').toObject().days;
      numberOfDaysMax.value = endDate.value.diff(val, 'days').toObject().days;
    },
  );
  watch(
    () => endDate.value,
    (val: DateTime) => {
      numberOfDaysMin.value = val.diff(startDate.value, 'days').toObject().days;
      numberOfDaysMax.value = val.diff(startDate.value, 'days').toObject().days;
    },
  );
  watch(
    () => selectedBuyerOption.value,
    (val) => {
      lastSelectedBuyerOption.value = val;

      if (isPreferredOrNGInternalDeal.value) {
        isFlexible.value = !isCuratorOrganization.value;
        initializeSettingsForNGOrPreferredInternalDeal(false);
        if (selectedBuyerOrganizationDSPs.value?.length === 0) {
          if (props.isNew && !isCopiedDeal.value && !deal.value.isCuratedDeal) {
            isCPMFromRateCard.value = true;
            cpmFixed.value = 0;
          } else {
            isCPMFromRateCard.value = deal.value.isCPMFromRateCard;
          }
        }
      }
      if (isNGExternalOrPreferredExternalDeal.value) {
        if (!isCopiedDeal.value && props.isNew) {
          initializeSettingsForNGOrPreferredExternalDeal();
        }
        isCPMFromRateCard.value = props.isNew && !deal.value.isCuratedDeal ? false : deal.value.isCPMFromRateCard;
        isForBidding.value = true;
        isFlexible.value = false;
      } else {
        isForBidding.value = false;
        minConcentrationFactor.value = 1;
      }
      if (isNGOrPreferredDeal.value) {
        setDefaultValueForHideUnitInformationFromBidRequests();
      }

      switch (val) {
        case PackageBuyerOptions.AvailableToAllBuyers:
        case PackageBuyerOptions.AvailableToAllExternalBuyers:
          selectedRestrictedBuyerOrganizationDSPs.value = [];
          buyerOrganizationDSPIDs.value = [];
          buyerExternalIDs.value = [];
          selectedRestrictedBuyerExternalExchanges.value = [];
          selectedRestrictedCuratorOrganizations.value = [];
          isInternalDSPSelected.value = false;
          isExternalDSPSelected.value = false;
          break;
        case PackageBuyerOptions.AvailableToSpecificBuyers:
          const tooltips = Array.from(window.document.getElementsByClassName('hs-tooltip'));
          tooltips.forEach(($el) => $el.remove());
          selectedRestrictedCuratorOrganizations.value = [];
          isInternalDSPSelected.value = false;
          isExternalDSPSelected.value = false;
          if (props.isNew) {
            setupSelectedBuyerOrganizationDSPs();
          }
          if (!isRestrictedExchangeSelected.value) {
            selectedRestrictedBuyerExternalExchanges.value = [];
          }
          break;
        case PackageBuyerOptions.AvailableToSpecificDsps:
          if (isRestrictedExchangeSelected.value) {
            selectedRestrictedBuyerExternalExchanges.value = [];
          }
          selectedRestrictedBuyerOrganizationDSPs.value = [];
          buyerOrganizationDSPIDs.value = [];
          selectedBuyerOrganizationDSPs.value = [];
          buyerExternalIDs.value = [];
          selectedRestrictedCuratorOrganizations.value = [];
          break;
        case PackageBuyerOptions.AvailableToSpecificCurators:
          selectedRestrictedBuyerOrganizationDSPs.value = [];
          selectedRestrictedBuyerExternalExchanges.value = [];
          buyerOrganizationDSPIDs.value = [];
          buyerExternalIDs.value = [];
          selectedBuyerOrganizationDSPs.value = [];
          isInternalDSPSelected.value = false;
          isExternalDSPSelected.value = false;
          // Also clear any demographic/audience targeting since curated deals can not add audience targeting on top of child deals that have their own targeting
          clearAudienceTargeting();
          break;
        default:
          selectedRestrictedBuyerOrganizationDSPs.value = [];
          selectedRestrictedCuratorOrganizations.value = [];
          buyerOrganizationDSPIDs.value = [];
          buyerExternalIDs.value = [];
          isInternalDSPSelected.value = false;
          isExternalDSPSelected.value = false;
          selectedBuyerExternalExchanges.value = [];
          break;
      }
      if (isNGExternalOrPreferredExternalDeal.value) {
        if (props.isNew && !props.isCloning) {
          let type = auctionTypeOptions.value.FirstPrice;
          isCPMFromRateCard.value = deal.value.isCuratedDeal ? deal.value.isCPMFromRateCard : false;
          if (
            val === PackageBuyerOptions.AvailableToSpecificBuyers &&
            selectedBuyerOrganizationDSPs.value?.length <= 1
          ) {
            type = auctionTypeOptions.value.FixedPrice;
          }
          auctionType.value = type;
        }
        const isOpen =
          val === PackageBuyerOptions.AvailableToAllExternalBuyers ||
          (val === PackageBuyerOptions.AvailableToSpecificDsps && isExternalDSPSelected.value);
        updatePackageIsOpen({
          package: deal.value,
          value: isOpen,
        });
      } else {
        updatePackageIsOpen({
          package: deal.value,
          value: false,
        });
      }
    },
  );
  watch(
    () => props.buyerOrganizationDSPs,
    () => {
      if (lastBuyerSearchTerm != null) {
        return;
      }

      // Remove selected buyers that no longer appear in the list.
      selectedBuyerOrganizationDSPs.value = selectedBuyerOrganizationDSPs.value.filter((x) =>
        props.buyerOrganizationDSPs.some((y) => y.id === x.id),
      );
    },
  );
  watch(
    () => isCPMFromRateCard.value,
    (val) => {
      // When creating a deal the cpmFixed stays null when switching to PG.
      if (!val && isGuaranteedDeal.value && cpmFixed.value == null) {
        cpmFixed.value = 0;
      }
    },
  );
  watch(
    () => dealCreationMode.value,
    async (val) => {
      // Creation mode is only applicable for curated deals
      if (!isCuratorOrganization) {
        await initializeDeal();
        return;
      }
      if (props.isNew && !props.isCloning) {
        selectedBuyerExternalExchanges.value = [];
        isInternalDSPSelected.value = false;
        isExternalDSPSelected.value = false;
      }
      // Emit to parents to restore original deal state (new/existing)
      emit('restoreDeal', true);
      setTimeout(async () => {
        await initializeDeal();
        updatePackageCurationType({
          package: deal.value,
          value: val === DealCreationModes.DEFAULT ? CurationType.PUBLISHER_DEALS : CurationType.EXTERNAL_SSP_DEALS,
        });
      }, 100);
    },
  );
  watch(
    () => dealLockedScopeType.value,
    (val, oldVal) => {
      // We should only modify for_bidding if the deal is specifically locked internally or externally.
      if (val === oldVal || val === null) return;
      isForBidding.value = val === DealScope.EXTERNAL;
    },
  );

  async function initializeDeal() {
    targetingSectionHelper.value = new TargetingSectionHelper(deal.value);

    const showDefaultPurchaseType =
      !isBeingCreatedFromPlan.value ||
      (isBeingCreatedFromPlan.value && purchaseType.value.value === PurchaseTypes.GUARANTEED);

    if (props.isNew && !props.isCloning && showDefaultPurchaseType) {
      onPurchaseTypeChanged([purchaseTypes.value.find((x) => x.value === PurchaseTypes.NON_GUARANTEED)]);
      setDefaultValueForHideUnitInformationFromBidRequests();
      $validator.init(deal.value);
      entityTimestamp.value = DateTime.fromISO(deal.value.timestamp.toISO());

      isCPMFromRateCard.value = isBeingCreatedFromPlan.value ? deal.value.isCPMFromRateCard : true;
      isFlexible.value = false;

      //
      // Curator needs to populate the media owners.
      //
      if (isCuratorOrganization.value) {
        if (deal.value.packages?.length > 0) {
          mediaOwnerIDs.value = deal.value.packages.map((x) => x.mediaOwnerIDs).flat();
        } else if (dealCreationMode.value === DealCreationModes.EXTERNAL) {
          mediaOwnerIDs.value = [];
        } else {
          handleMediaOwnersSelectionChanged(props.allMediaOwners);
        }
      }
    } else {
      $validator.rebindModel(deal.value);
      entityTimestamp.value = DateTime.fromISO(deal.value.timestamp?.toISO());
      setupSelectedBuyerOrganizationDSPs();

      //
      // Figure out if some fields are fixed or a range.
      //
      adDurationType.value = adDurationMin.value === adDurationMax.value ? 'fixed' : 'range';
      minSecondsBetweenPlaysType.value =
        minSecondsBetweenPlaysMin.value === minSecondsBetweenPlaysMax.value ? 'fixed' : 'range';
      budgetType.value = budgetMin.value === budgetMax.value ? 'fixed' : 'range';
      impressionsType.value =
        deal.value.marketTargetedImpressionsMin === deal.value.marketTargetedImpressionsMax ? 'fixed' : 'range';

      //
      // Figure out if the deal was originally setup to be flexible or not.
      //
      isFlexible.value =
        !isCuratorOrganization.value &&
        !(
          unitAssignmentsOverridable.value === false &&
          adDurationOverridable.value === false &&
          adDurationType.value === 'fixed' &&
          minSecondsBetweenPlaysOverridable.value === false &&
          minSecondsBetweenPlaysType.value === 'fixed' &&
          networksOverridable.value === false &&
          deal.value.packageTargetingCriteriaOverridable === false &&
          mediaTypesOverridable.value === false &&
          locationsOverridable.value === false &&
          demographicsOverridable.value === false &&
          budgetOverridable.value === false &&
          impressionsOverridable.value === false &&
          budgetType.value === 'fixed' &&
          impressionsType.value === 'fixed'
        ) &&
        !isPurchaseTypeCadence.value;

      if (deal.value.buyerOrganizationDSPIDs?.length > 0) {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToSpecificBuyers;
      } else if (deal.value.buyerExternalExchangeIDs?.length > 0) {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToSpecificDsps;
        isExternalDSPSelected.value = true;
      } else if (deal.value.curatorOrganizationIDs?.length > 0) {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToSpecificCurators;
        isForBidding.value = null;
      } else if (deal.value.isOpen) {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToAllExternalBuyers;
      } else if (deal.value.isPreferredOrNGInternalDeal) {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToSpecificDsps;
        isInternalDSPSelected.value = true;
      } else {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToAllBuyers;
      }

      if (isPreferredOrNGInternalDeal.value) {
        initializeSettingsForNGOrPreferredInternalDeal(false);
      }
      $validator.init(deal.value);

      queueRefreshAudienceStats();
    }
    if (isNGExternalOrPreferredExternalDeal.value) {
      // No buyer definable / predefined concept for NG & Preferred PMP
      isFlexible.value = false;
      auctionType.value =
        props.isNew && !props.isCloning ? auctionTypeOptions.value.FixedPrice : deal.value.auctionType;
      if (props.isNew && !props.isCloning && !deal.value.isCuratedDeal) {
        // pricingTypeInternal.value = pricingTypeOptions.value.Static;
        isCPMFromRateCard.value = false;
      } else {
        isCPMFromRateCard.value = deal.value.isCPMFromRateCard;
        // pricingTypeInternal.value = deal.value.isCPMFromRateCard
        //   ? pricingTypeOptions.value.RateCard
        //   : pricingTypeOptions.value.Static;
      }
    }
    isReady.value = true;

    if (props.isOrganization) {
      updatePackageMediaOwnerIDs({
        package: deal.value,
        value: mediaOwnerIDs.value,
      });
    }

    organizationExternalExchangesInternal.value = organizationExternalExchanges.value.map((x) => ({
      ...x,
      isDisabled: false,
    }));

    if (!props.isNew || isCopiedDeal.value) {
      if (isInternalDSPSelected.value) {
        const hivestackDSP = organizationExternalExchangesInternal.value.filter((x) => x.code === 'INTERNAL');
        onSelectedBuyerExternalExchangesChanged(hivestackDSP);
      } else if (isXandrExchangeSelected.value) {
        const xandrDSP = organizationExternalExchangesInternal.value.filter((x) => x.code.includes('XANDR'));
        onSelectedBuyerExternalExchangesChanged(xandrDSP);
      } else if (isDV360ExchangeSelected.value) {
        const dv360DSP = organizationExternalExchangesInternal.value.filter((x) => x.code.includes('DV360'));
        onSelectedBuyerExternalExchangesChanged(dv360DSP);
      }
    }

    // NG deals: Allow MO editing demographics if they are defined as predefined value
    originalDemographicTargetsOverridable.value = deal.value.demographicTargetsOverridable;

    // NG, Prefered Deal no longer use overridable settings, now we need to check each targeting to pre-fill the form
    if (isPreferredOrNGInternalDeal.value && (!props.isNew || isCopiedDeal.value || isBeingCreatedFromPlan.value)) {
      if (organizationSettings.enableMoFixedAdDurationOverride) {
        hasAdDuration.value = deal.value.adDurationMin != null || deal.value.adDurationMax != null;
      } else {
        hasAdDuration.value = deal.value.adDurationMin != null && deal.value.adDurationMin !== 0;
      }
      hasMinSecondsBetweenPlays.value =
        (deal.value.minSecondsBetweenPlaysMin != null && deal.value.minSecondsBetweenPlaysMin !== 0) ||
        (deal.value.minSecondsBetweenPlaysMax != null && deal.value.minSecondsBetweenPlaysMax !== 0);
    }

    if (!props.isNew) {
      isDealBought.value = isCuratorOrganization.value ? false : await getIsDealBought({ id: deal.value.id });
      isCPMFromRateCard.value = deal.value.isCPMFromRateCard;
    }

    if (isNGExternalOrPreferredExternalDeal.value) {
      if (props.isNew && isCuratorOrganization.value && !isCopiedDeal.value) {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToSpecificDsps;
      } else if (deal.value.buyerExternalExchangeIDs?.length > 0) {
        const organizationExternalExchangeIDs = organizationExternalExchanges.value.map((x) => x.externalExchangeID);
        // means.value a package has an exchange selected but it's unlinked. We need to address in.value front end.
        isMissingSomeSelectedExchange.value = deal.value.buyerExternalExchangeIDs?.some(
          (x) => !organizationExternalExchangeIDs.includes(x),
        );
        isMissingAllSelectedExchange.value = // deal.value.buyerExternalExchangeIDs?.length === 0 ||
          deal.value.buyerExternalExchangeIDs?.every((x) => !organizationExternalExchangeIDs.includes(x));
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToSpecificDsps;
        if (isMissingAllSelectedExchange.value && organizationExternalExchanges.value.length === 0) {
          selectedBuyerOption.value = PackageBuyerOptions.AvailableToSpecificBuyers;
        }
        selectedBuyerExternalExchanges.value = organizationExternalExchanges.value.filter((x) =>
          deal.value.buyerExternalExchangeIDs.includes(x.externalExchangeID),
        );
      } else if (deal.value.buyerOrganizationDSPIDs?.length > 0) {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToSpecificBuyers;
      } else if (deal.value.curatorOrganizationIDs?.length > 0) {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToSpecificCurators;
        selectedRestrictedCuratorOrganizations.value = curatorOrganizations.value.filter((x) =>
          deal.value.curatorOrganizationIDs.includes(x.curatorOrganizationID),
        );
        isForBidding.value = null;
      } else if (deal.value.isOpen) {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToAllExternalBuyers;
      } else {
        selectedBuyerOption.value = PackageBuyerOptions.AvailableToSpecificDsps;
      }
    }
  }

  onMounted(async () => {
    await waitForPendingQueries([externalExchangesQuery, externalSSPsQuery]);

    isMounted.value = true;

    // Pick up the purchase type from the route when a planID is supplied in props.
    if (isBeingCreatedFromPlan.value) {
      if (router.currentRoute.value.params.packagePurchaseType != null) {
        purchaseType.value = purchaseTypes.value.find(
          (x) => x.value === router.currentRoute.value.params.packagePurchaseType,
        );
      }
    }

    // Will trigger the watcher to initialize the deal
    dealCreationMode.value =
      isCuratorOrganization && deal.value.curationType === CurationType.EXTERNAL_SSP_DEALS
        ? DealCreationModes.EXTERNAL
        : DealCreationModes.DEFAULT;

    if (props.isNew) {
      // Focus on name field if new deal
      setTimeout(() => window.document.getElementById('edit_package_name').focus(), 100);
    }
    loadBuyerOptions.value = true;
    window.addEventListener('wheel', onWindowMouseWheel);

    // Validate that if the deal is a CD, that the buyer/dsp restrictions and selected PDs are compatible
    if (deal.value.isCuratedDeal) {
      isIncompatibleSelectionModalVisible.value =
        hasMismatchedPublisherDeals.value || hasIncompatibleRestrictions.value;
    }

    // Add listener to see if active previewed deal is changed and handle accordingly
    GlobalEventBus.$on(GlobalEvents.activeDealchanged, (p: Package) => {
      activeDeal.value = p;
    });

    //
    // When editing, make sure the original object has not changed after the initial load.
    //
    didDetectUnexpectedChanges.value = !deal.value.isPristine();
    if (didDetectUnexpectedChanges.value) {
      UserNotifications.unexpectedChangeDetected();
    }
  });

  onBeforeUnmount(() => {
    isMounted.value = false;
    window.removeEventListener('wheel', onWindowMouseWheel);
  });

  onBeforeRouteLeave((to, from, next) => {
    if (Math.abs(entityTimestamp.value.diff(deal.value.timestamp, 'milliseconds').milliseconds) < 1000) {
      next();
    } else {
      isConfirmLosingChangesModalVisible.value = true;
      confirmLosingChangesCallback.value = next;
    }
  });

  defineExpose({
    saveChangesModalCancelled,
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .form-wrapper {
    display: flex;
    flex: 1;
    flex-direction: column;
  }

  #app {
    .form-and-stats {
      display: flex;
      flex-direction: row;
    }

    .form-container {
      flex-grow: 1;
    }

    .alert {
      margin-top: 1.6rem;
    }

    .missing-dimensions {
      text-align: center;
      font-size: 1.2rem;
      padding-top: 1rem;
      border-top: 0.1rem solid $borders-light;

      .unit-dimensions-container {
        margin: 1rem 0;
      }
    }

    .multiselect.is-danger,
    .well.is-danger {
      border: 1px solid red;
      border-radius: 5px;
    }

    #xandr_dv_deal_buyer_restrictions {
      .multiselect {
        .multiselect__tags {
          border: 1px solid red;
        }
      }
    }

    .right-arrow {
      display: flex;
      justify-content: center;
      align-items: center;

      svg {
        width: 4em;
        height: 4em;
        fill: #aaaaaa;
      }
    }

    .pane-group {
      label {
        min-width: 36%;
        display: flex;
        flex-direction: column;
      }

      div {
        display: flex;
        width: 100%;
        flex-direction: column;
      }
    }

    .vue-slider-label {
      width: 20rem;
      display: block;
      margin-left: -10rem;
      text-align: center;
      margin-top: 0.6rem;

      &.left {
        text-align: left;
        margin-left: -0.5rem;
      }

      &.right {
        text-align: right;
        margin-left: -19rem;
      }

      .field-help {
        display: inline-block;
        margin-left: 0.3rem;
        top: 0.3rem;
        position: relative;
      }
    }

    .vue-slider-label-marker {
      width: 0.1rem;
      height: 1rem;
      background: #555;
      margin-top: 1.6rem;
      margin-left: 0.3rem;
    }

    .refresh-cadence-info-container {
      display: flex;
      position: fixed;
      right: 5.6rem;
      margin-top: 10.2rem;
      width: 33rem;
      top: 0;
      padding: 1rem;
      font-size: 0.9em;

      .floating-pane {
        .floating-pane-content {
          width: 33rem;
          flex-direction: initial;
          overflow: hidden;

          .icon-container {
            width: 10rem;
            padding: 4rem 1rem;

            svg {
              width: 8rem;
              height: 8rem;
              fill: #fff;
              stroke: #fff;
            }

            color: #fff;
            background-color: var(--primaryColor);
          }

          .update-cadence-msg {
            width: 23rem;

            .header {
              padding: 0.8rem 1rem;

              .left-col {
                width: 80%;
              }

              .right-col {
                width: 20%;
              }
            }

            .body {
              padding: 1.5rem;
            }

            .footer {
              padding: 1.5rem;
            }
          }
        }
      }
    }

    .multiselect {
      .multiselect__tags {
        border: 1px solid red;
      }
    }
  }
</style>
